import * as _Astronomy from 'astronomy-engine';
const Astronomy: any = (_Astronomy as any).Observer ? _Astronomy : ((_Astronomy as any).default || _Astronomy);
import { NAKSHATRAS, ZODIAC_SIGNS, ZODIAC_LORDS, getLahiriAyanamsha, calculateAscendant, formatDegree, calculateLivePlanetaryEphemeris } from './vedicEngine';
import {
  TaraType,
  TaraBalaResult,
  ActivityPreference,
  MuhurtaScore,
  MuhurtaAnalysis,
  MuhurtaSearchParams,
  VedicShuddhiOptions,
  LagnaShuddhiDetails,
  AuspiciousYogaInfo,
  MuhurtaPillarsScore,
  LagnaScoreBreakdown,
  YogaRuleResult,
  KaranaRuleResult,
  YogaRuleClassification,
  KaranaRuleClassification,
  MuhurtaLocationContext,
  MuhurtaEvaluationSemantics
} from '../types/muhurta';
import { PlanetName, PlanetPosition, ZodiacSign, CompleteKundali } from '../types/kundali';
import { formatTime } from './dateUtils';
import {
  calculateAuthoritativePanchanga,
  calculateAuthoritativeSunMoon,
  calculateCanonicalChoghadiya,
  PitruPakshaContext,
  VratObservanceInfo,
  SankrantiInfo
} from './panchangaEngine';
import { evaluatePhase16SolarLunarRules } from './muhurtaPhase16SolarLunarEngine';
import { evaluatePhase17CalendarRules } from './muhurtaPhase17LunarSolarEngine';
import { evaluateSamskaraMuhurta, SamskaraEvaluationResult } from './muhurtaPhase18SamskaraEngine';
import { resolveMuhurtaFactors, FactorEvidence } from './muhurtaPhase19ResolutionEngine';


// --- CONFIGURABLE VEDIC SHUDDHI CRITERIA & PRESETS ---

export const DEFAULT_SHUDDHI_OPTIONS: VedicShuddhiOptions = {
  requireSthiraLagna: false,
  requireCharaLagna: false,
  ashtamaShuddhi: true,
  upachayaMalefics: true,
  kendraBenefics: true,
  gandantaFilter: true,
  vishaGhatiFilter: false,
  boostSiddhiYogas: true,
  taraBalaEnforced: true,
  chandraBalaEnforced: true,
};

export const SHUDDHI_PRESETS: Record<string, { id: string; label: string; hiLabel: string; description: string; options: VedicShuddhiOptions }> = {
  lifelong_business: {
    id: 'lifelong_business',
    label: 'Lifelong Business / Enterprise',
    hiLabel: 'चिरस्थायी व्यापार (स्थिर लग्न)',
    description: 'Enforces Sthira (Fixed) Lagna, Ashtama Shuddhi (8H clean), Upachaya malefics, and Gandanta block for unshakeable permanence.',
    options: {
      requireSthiraLagna: true,
      requireCharaLagna: false,
      ashtamaShuddhi: true,
      upachayaMalefics: true,
      kendraBenefics: true,
      gandantaFilter: true,
      vishaGhatiFilter: true,
      boostSiddhiYogas: true,
      taraBalaEnforced: true,
      chandraBalaEnforced: true,
    }
  },
  quick_trade: {
    id: 'quick_trade',
    label: 'Quick Trade & Logistics',
    hiLabel: 'शीघ्र व्यापार व लॉजिस्टिक्स (चर लग्न)',
    description: 'Enforces Chara (Movable) Lagna for high asset turnover, delivery fleets, transport, and rapid transactional expansion.',
    options: {
      requireSthiraLagna: false,
      requireCharaLagna: true,
      ashtamaShuddhi: true,
      upachayaMalefics: true,
      kendraBenefics: true,
      gandantaFilter: true,
      vishaGhatiFilter: false,
      boostSiddhiYogas: true,
      taraBalaEnforced: true,
      chandraBalaEnforced: true,
    }
  },
  strict_shastric: {
    id: 'strict_shastric',
    label: 'Strict Classical Pandit',
    hiLabel: 'कठोर शास्त्रीय पद्धति',
    description: 'Maximum classical Vedic purity: filters Gandanta, Visha Ghati, Ashtama dosha, and strictly aligns Navatara and Gochar.',
    options: {
      requireSthiraLagna: true,
      requireCharaLagna: false,
      ashtamaShuddhi: true,
      upachayaMalefics: true,
      kendraBenefics: true,
      gandantaFilter: true,
      vishaGhatiFilter: true,
      boostSiddhiYogas: true,
      taraBalaEnforced: true,
      chandraBalaEnforced: true,
    }
  },
  broad_search: {
    id: 'broad_search',
    label: 'Broad / Relaxed Search',
    hiLabel: 'व्यापक व सुगम खोज',
    description: 'Relaxes rigid structural restrictions to discover viable and auspicious windows within urgent or restricted dates.',
    options: {
      requireSthiraLagna: false,
      requireCharaLagna: false,
      ashtamaShuddhi: false,
      upachayaMalefics: false,
      kendraBenefics: true,
      gandantaFilter: false,
      vishaGhatiFilter: false,
      boostSiddhiYogas: true,
      taraBalaEnforced: true,
      chandraBalaEnforced: true,
    }
  }
};

// --- TARA BALA ENGINE ---

export const TARA_NAMES: TaraType[] = [
  'Janma', 'Sampat', 'Vipat', 'Kshema', 'Pratyari', 'Sadhaka', 'Naidhana', 'Mitra', 'Param Mitra'
];

export const TARA_MEANINGS: { [key in TaraType]: string } = {
  Janma: 'Birth / Foundation / Identity',
  Sampat: 'Wealth / Resources / Acquisition',
  Vipat: 'Troubles / Loss / Unexpected hurdles',
  Kshema: 'Well-being / Comfort / Protection',
  Pratyari: 'Opposition / Enemies / Conflicts',
  Sadhaka: 'Achievement / Success / Accomplishment',
  Naidhana: 'Danger / Extreme caution / Crisis',
  Mitra: 'Friendship / Support / Progress',
  'Param Mitra': 'Great Friendship / Divine Grace / Ultimate Support'
};

export function calculateTaraBala(janmaNakIdx: number, targetNakIdx: number): TaraBalaResult {
  const diff = (targetNakIdx - janmaNakIdx + 27) % 27;
  const count = diff + 1;
  const cycleIndex = (count - 1) % 9;
  const taraName = TARA_NAMES[cycleIndex];

  const countMod = count % 9 === 0 ? 9 : count % 9;
  let suitability: TaraBalaResult['suitability'] = 'Neutral';
  if ([2, 4, 6, 8, 9].includes(countMod)) {
    suitability = countMod === 2 || countMod === 6 || countMod === 9 ? 'Excellent' : 'Good';
  } else if ([3, 5, 7].includes(countMod)) {
    suitability = countMod === 7 ? 'Danger' : 'Poor';
  } else {
    suitability = 'Neutral';
  }

  return {
    taraName,
    taraCount: count,
    meaning: TARA_MEANINGS[taraName],
    suitability
  };
}

// --- NAKSHATRA CLASSIFICATION (Sthira, Chara, etc.) ---

export interface NakshatraTypeInfo {
  category: 'Dhruva (Fixed/Sthira)' | 'Chara (Movable/Chara)' | 'Ugra (Fierce)' | 'Mishra (Mixed/Sadharana)' | 'Laghu (Light/Swift)' | 'Mridu (Tender/Soft)' | 'Tikshna (Sharp/Dreadful)';
  sanskritType: string;
  meaning: string;
  suitableFor: string[];
  description: string;
}

export const NAKSHATRA_TYPE_CLASSIFICATION: Record<number, NakshatraTypeInfo> = {
  // 0: Ashwini -> Laghu (Light/Swift)
  0: {
    category: 'Laghu (Light/Swift)',
    sanskritType: 'लघु / क्षिप्र (Laghu / Kshipra)',
    meaning: 'Swift, dynamic, agile and quick-moving energy',
    suitableFor: ['Medical treatments', 'Starting journeys/travel', 'Buying vehicles', 'Giving/taking loans', 'Sports & transactions'],
    description: 'Auspicious for rapid-turnaround actions, business deals, buying vehicles, beginning travel, and administering medicine.'
  },
  // 1: Bharani -> Ugra (Fierce)
  1: {
    category: 'Ugra (Fierce)',
    sanskritType: 'उग्र / क्रूर (Ugra / Krura)',
    meaning: 'Fierce, intense, assertive, and cutting energy',
    suitableFor: ['Demolition', 'Legal battles', 'Weapons/surgery', 'Research & diagnostics', 'Debates & competitive actions'],
    description: 'Best for aggressive or competitive works, surgical procedures, boundary enforcement, and confronting adversaries.'
  },
  // 2: Krittika -> Mishra (Mixed/Sadharana)
  2: {
    category: 'Mishra (Mixed/Sadharana)',
    sanskritType: 'मिश्र / साधारण (Mishra / Sadharana)',
    meaning: 'Dual nature combining sharp purification with constructive potential',
    suitableFor: ['Fire rituals (Homa/Havan)', 'Purchasing electrical goods/machinery', 'Welding & metallurgy', 'Purification rites'],
    description: 'Good for furnace work, metallurgy, performing sacred yajnas, electrical installations, and routine constructive actions.'
  },
  // 3: Rohini -> Dhruva (Fixed/Sthira)
  3: {
    category: 'Dhruva (Fixed/Sthira)',
    sanskritType: 'ध्रुव / स्थिर (Dhruva / Sthira)',
    meaning: 'Firm, immovable, permanent, and long-lasting energy',
    suitableFor: ['Griha Pravesh (House warming)', 'Foundation laying', 'Buying property/land', 'Coronation/swearing-in', 'Marriage', 'Planting trees'],
    description: 'The premier Nakshatra for all actions intended to produce permanent, stable, generational, and unchanging prosperity.'
  },
  // 4: Mrigashira -> Mridu (Tender/Soft)
  4: {
    category: 'Mridu (Tender/Soft)',
    sanskritType: 'मृदु / मैत्र (Mridu / Maitra)',
    meaning: 'Gentle, affectionate, aesthetic, and peaceful energy',
    suitableFor: ['Marriage', 'Wearing new clothes/jewelry', 'Fine arts & music', 'Romance & friendships', 'Starting creative hobbies'],
    description: 'Favored for partnerships, weddings, purchasing jewelry, artistic performances, beginning new friendships, and romantic endeavors.'
  },
  // 5: Ardra -> Tikshna (Sharp/Dreadful)
  5: {
    category: 'Tikshna (Sharp/Dreadful)',
    sanskritType: 'तीक्ष्ण / दारुण (Tikshna / Daruna)',
    meaning: 'Sharp, piercing, stormy, and transformative energy',
    suitableFor: ['Exorcisms', 'Tantric sadhana', 'Demolition', 'Separation/divorce filing', 'Deep research & forensics'],
    description: 'Suitable for harsh or surgical tasks, scientific research, dismantling old structures, and confronting deep-rooted obstacles.'
  },
  // 6: Punarvasu -> Chara (Movable/Chara)
  6: {
    category: 'Chara (Movable/Chara)',
    sanskritType: 'चर / चल (Chara / Chala)',
    meaning: 'Movable, renewing, flexible, and progressive motion',
    suitableFor: ['Commencing travel/relocation', 'Purchasing vehicles', 'Gardening & sowing', 'Renovation', 'Acquiring mobile assets'],
    description: 'Governed by movement; optimal for beginning journeys, purchasing vehicles, moving residences, and activities requiring adaptivity.'
  },
  // 7: Pushya -> Laghu (Light/Swift) - also Guru of Nakshatras
  7: {
    category: 'Laghu (Light/Swift)',
    sanskritType: 'लघु / क्षिप्र (Laghu / Kshipra)',
    meaning: 'Nourishing, auspicious, spiritual, and swiftly fulfilling',
    suitableFor: ['Business beginnings', 'Buying gold/investments', 'Spiritual initiation (Diksha)', 'Commencing education', 'Signing agreements'],
    description: 'Considered the most auspicious among stars for commerce, launching businesses, spiritual initiations, and signing contracts (except wedding rituals).'
  },
  // 8: Ashlesha -> Tikshna (Sharp/Dreadful)
  8: {
    category: 'Tikshna (Sharp/Dreadful)',
    sanskritType: 'तीक्ष्ण / दारुण (Tikshna / Daruna)',
    meaning: 'Coiled, secretive, piercing, and protective serpent energy',
    suitableFor: ['Competitive strategies', 'Pest control & poisons', 'Filing injunctions', 'Occult study', 'Surveillance'],
    description: 'Use for secretive investigations, filing complaints, dealing with toxic situations, and self-defensive strategic measures.'
  },
  // 9: Magha -> Ugra (Fierce)
  9: {
    category: 'Ugra (Fierce)',
    sanskritType: 'उग्र / क्रूर (Ugra / Krura)',
    meaning: 'Regal, ancestral, commanding, and resolute power',
    suitableFor: ['Honoring ancestors (Pitr-karya)', 'Assuming high leadership positions', 'Military actions', 'Demolition'],
    description: 'Excellent for ancestral rites, authoritative command, competitive assertions, and taking charge of family or corporate dynasties.'
  },
  // 10: Purva Phalguni -> Ugra (Fierce)
  10: {
    category: 'Ugra (Fierce)',
    sanskritType: 'उग्र / क्रूर (Ugra / Krura)',
    meaning: 'Warm, intense, passionate, and vibrant creative force',
    suitableFor: ['Entertainment & dance', 'Passionate negotiations', 'Manufacturing luxury goods', 'Dealing with government'],
    description: 'Favorable for creative endeavors, leisure pursuits, dramatic arts, and overcoming opponents through charm or charisma.'
  },
  // 11: Uttara Phalguni -> Dhruva (Fixed/Sthira)
  11: {
    category: 'Dhruva (Fixed/Sthira)',
    sanskritType: 'ध्रुव / स्थिर (Dhruva / Sthira)',
    meaning: 'Permanent, benevolent, philanthropic, and lasting stability',
    suitableFor: ['Marriage', 'Griha Pravesh', 'Foundation stone laying', 'Long-term contracts', 'Opening trusts/charities'],
    description: 'One of the prime fixed stars ensuring longevity in marriages, business alliances, foundation-stone ceremonies, and permanent dwellings.'
  },
  // 12: Hasta -> Laghu (Light/Swift)
  12: {
    category: 'Laghu (Light/Swift)',
    sanskritType: 'लघु / क्षिप्र (Laghu / Kshipra)',
    meaning: 'Handcraft, deft, clever, and skillful rapid execution',
    suitableFor: ['Business transactions', 'Artisanal work/crafts', 'Signing agreements', 'Commencing study', 'Buying gold/silver'],
    description: 'Ideal for commerce, fine manual handiwork, signing contracts, beginning educational courses, and swift transactions.'
  },
  // 13: Chitra -> Mridu (Tender/Soft)
  13: {
    category: 'Mridu (Tender/Soft)',
    sanskritType: 'मृदु / मैत्र (Mridu / Maitra)',
    meaning: 'Brilliant, aesthetic, multi-faceted, and artistic creation',
    suitableFor: ['Interior decoration', 'Designing & architecture', 'Fashion & styling', 'Jewelry purchase', 'Visual arts & UI/UX'],
    description: 'Auspicious for interior design, fine clothing, buying jewelry, digital/architectural design, and creative collaborations.'
  },
  // 14: Swati -> Chara (Movable/Chara)
  14: {
    category: 'Chara (Movable/Chara)',
    sanskritType: 'चर / चल (Chara / Chala)',
    meaning: 'Wind-like, independent, movable, and expanding journey',
    suitableFor: ['Air travel/long voyages', 'Purchasing cars/vehicles', 'Foreign trade & export', 'Acquiring software/digital tools'],
    description: 'The benchmark movable star: highly recommended for overseas journeys, transport, buying vehicles, and dynamic marketing campaigns.'
  },
  // 15: Vishakha -> Mishra (Mixed/Sadharana)
  15: {
    category: 'Mishra (Mixed/Sadharana)',
    sanskritType: 'मिश्र / साधारण (Mishra / Sadharana)',
    meaning: 'Goal-oriented, triumphant, bifurcated, and resilient',
    suitableFor: ['Celebrations of achievement', 'Targeted business launches', 'Fulfilling vows', 'Agrochemicals & distillation'],
    description: 'Empowers decisive achievement, celebrations of success, agricultural milestones, and tasks requiring sharp perseverance.'
  },
  // 16: Anuradha -> Mridu (Tender/Soft)
  16: {
    category: 'Mridu (Tender/Soft)',
    sanskritType: 'मृदु / मैत्र (Mridu / Maitra)',
    meaning: 'Friendship, devotion, alliance, and cooperative harmony',
    suitableFor: ['Marriage ceremonies', 'Friendship & partnership pacts', 'Travel & pilgrimages', 'Opening savings accounts'],
    description: 'Highly praised in Muhurta shastras for weddings, forming beneficial alliances, travel, and religious/spiritual gatherings.'
  },
  // 17: Jyeshta -> Tikshna (Sharp/Dreadful)
  17: {
    category: 'Tikshna (Sharp/Dreadful)',
    sanskritType: 'तीक्ष्ण / दारुण (Tikshna / Daruna)',
    meaning: 'Senior, formidable, protective, and cutting edge',
    suitableFor: ['Defensive tactics', 'Confronting adversaries', 'High-stakes negotiations', 'Severing ties', 'Military commands'],
    description: 'Best utilized for protective defenses, competitive leadership, breaking unfair contracts, and decisive organizational restructures.'
  },
  // 18: Mula -> Tikshna (Sharp/Dreadful)
  18: {
    category: 'Tikshna (Sharp/Dreadful)',
    sanskritType: 'तीक्ष्ण / दारुण (Tikshna / Daruna)',
    meaning: 'Root, extraction, unearthing, and foundational deconstruction',
    suitableFor: ['Demolition & uprooting', 'Drilling/mining', 'Subterranean exploration', 'Root-cause analysis & auditing'],
    description: 'Suitable for laying bare truths, diagnostic audits, investigative research, excavation, and clearing away unwanted accumulations.'
  },
  // 19: Purva Ashadha -> Ugra (Fierce)
  19: {
    category: 'Ugra (Fierce)',
    sanskritType: 'उग्र / क्रूर (Ugra / Krura)',
    meaning: 'Invincible, aqueous, bold, and energetic determination',
    suitableFor: ['Water projects & irrigation', 'Maritime activities', 'Confronting competition', 'Bold marketing blitzes'],
    description: 'Favored for marine ventures, boldness in competitive markets, purification rituals, and water-related infrastructural initiatives.'
  },
  // 20: Uttara Ashadha -> Dhruva (Fixed/Sthira)
  20: {
    category: 'Dhruva (Fixed/Sthira)',
    sanskritType: 'ध्रुव / स्थिर (Dhruva / Sthira)',
    meaning: 'Universal victory, enduring, righteous, and permanent stability',
    suitableFor: ['Laying cornerstones', 'Griha Pravesh', 'Swearing in oaths', 'Founding companies & corporations', 'Marriage'],
    description: 'Supreme fixed star ensuring perpetual victory and unshakeable longevity for enterprises, residences, and covenants.'
  },
  // 21: Shravana -> Chara (Movable/Chara)
  21: {
    category: 'Chara (Movable/Chara)',
    sanskritType: 'चर / चल (Chara / Chala)',
    meaning: 'Listening, learning, mobile wisdom, and oral transmission',
    suitableFor: ['Starting studies/webinars', 'Beginning journeys', 'Purchasing cars/mobility', 'Podcasting & publishing', 'Consultations'],
    description: 'Auspicous for commencing education, broadcast media, acquiring vehicles, pilgrimages, and dynamic intellectual pursuits.'
  },
  // 22: Dhanishta -> Chara (Movable/Chara)
  22: {
    category: 'Chara (Movable/Chara)',
    sanskritType: 'चर / चल (Chara / Chala)',
    meaning: 'Wealth, rhythm, symphony, and dynamic velocity',
    suitableFor: ['Musical performances', 'Buying vehicles', 'Moving to new cities', 'Public relations', 'Financial transactions'],
    description: 'Brings dynamic momentum, rhythm, and fast prosperity; perfect for travel, transport, acquiring vehicles, and musical milestones.'
  },
  // 23: Shatabhisha -> Chara (Movable/Chara)
  23: {
    category: 'Chara (Movable/Chara)',
    sanskritType: 'चर / चल (Chara / Chala)',
    meaning: 'A hundred healers, oceanic, expansive, and free-flowing',
    suitableFor: ['Starting medical therapies', 'Holistic health retreats', 'Water filtration/oceanic projects', 'Aviation travel'],
    description: 'Optimal for embarking on healing journeys, medical treatments, purchasing vehicles, and endeavors involving movement and water.'
  },
  // 24: Purva Bhadrapada -> Ugra (Fierce)
  24: {
    category: 'Ugra (Fierce)',
    sanskritType: 'उग्र / क्रूर (Ugra / Krura)',
    meaning: 'Transformative, austere, intense, and purificatory fire',
    suitableFor: ['Detoxification & fasting', 'Ascetic retreats', 'Deep psychological work', 'Ending unproductive ventures'],
    description: 'Used for intense spiritual austerity, eliminating bad habits, terminating defunct partnerships, and deep transformative work.'
  },
  // 25: Uttara Bhadrapada -> Dhruva (Fixed/Sthira)
  25: {
    category: 'Dhruva (Fixed/Sthira)',
    sanskritType: 'ध्रुव / स्थिर (Dhruva / Sthira)',
    meaning: 'Auspicious depths, enduring patience, and permanent blessings',
    suitableFor: ['Griha Pravesh', 'Marriage', 'Coronation/leadership', 'Buying fixed real estate', 'Foundations & temples'],
    description: 'Highly auspicious fixed star producing profound stability, longevity, financial grounding, and enduring family happiness.'
  },
  // 26: Revati -> Mridu (Tender/Soft)
  26: {
    category: 'Mridu (Tender/Soft)',
    sanskritType: 'मृदु / मैत्र (Mridu / Maitra)',
    meaning: 'Sweet, protective, nurturing, and gracefully concluding',
    suitableFor: ['Weddings', 'Wearing fine gems/garments', 'Commencing artistic work', 'Adopting pets/animals', 'Beginning travel'],
    description: 'The final, gentle Nakshatra of the zodiac; celebrated for weddings, acquiring precious stones, artistic debuts, and safe journeys.'
  }
};

export function getNakshatraType(nakIdx: number): NakshatraTypeInfo {
  const normIdx = ((nakIdx % 27) + 27) % 27;
  return NAKSHATRA_TYPE_CLASSIFICATION[normIdx] || {
    category: 'Mishra (Mixed/Sadharana)',
    sanskritType: 'मिश्र (Mishra)',
    meaning: 'Standard energetic balance',
    suitableFor: ['General activities'],
    description: 'General auspicious activity.'
  };
}

/**
 * Canonical Nakshatra Transliteration Normalization (F-MUHURTA-01 / MUHURTA-IDEA-001)
 * Maps common Sanskrit/IAST/Anglicized variants to canonical 27 Nakshatra names.
 */
export function normalizeNakshatraName(name: string): string {
  if (!name || typeof name !== 'string') return '';
  const clean = name.trim().toLowerCase().replace(/[^a-z]/g, '');
  const map: Record<string, string> = {
    mrigashirsha: 'Mrigashira',
    mrigasira: 'Mrigashira',
    mrigashira: 'Mrigashira',
    sravana: 'Shravana',
    shravana: 'Shravana',
    aswini: 'Ashwini',
    ashwini: 'Ashwini',
    jyeshta: 'Jyeshtha',
    jyeshtha: 'Jyeshtha',
    satabhisha: 'Shatabhisha',
    shatabhisha: 'Shatabhisha',
    dhanistha: 'Dhanishta',
    dhanishta: 'Dhanishta',
    punardasu: 'Punarvasu',
    punarvasu: 'Punarvasu',
    uttaraphalguni: 'Uttara Phalguni',
    uttaraashadha: 'Uttara Ashadha',
    uttarabhadrapada: 'Uttara Bhadrapada',
    purvaphalguni: 'Purva Phalguni',
    purvaashadha: 'Purva Ashadha',
    purvabhadrapada: 'Purva Bhadrapada'
  };
  return map[clean] || name.trim();
}

// --- ACTIVITY RULES ENGINE ---

export const ACTIVITY_PREFERENCES: ActivityPreference[] = [
  { activityId: 'domain_purchase', label: 'Register/Purchase Domain', primaryTaras: ['Sampat', 'Sadhaka', 'Kshema'], secondaryTaras: ['Mitra', 'Param Mitra'] },
  { activityId: 'launch_website', label: 'Launch Website', primaryTaras: ['Sampat', 'Sadhaka', 'Param Mitra'], secondaryTaras: ['Kshema', 'Mitra'] },
  { activityId: 'launch_app', label: 'Launch Mobile App', primaryTaras: ['Sampat', 'Sadhaka', 'Param Mitra'], secondaryTaras: ['Kshema', 'Mitra'] },
  { activityId: 'start_business', label: 'Start Business', primaryTaras: ['Sampat', 'Sadhaka', 'Param Mitra'], secondaryTaras: ['Kshema', 'Mitra'] },
  { activityId: 'register_company', label: 'Register Company', primaryTaras: ['Sampat', 'Sadhaka', 'Kshema'], secondaryTaras: ['Mitra', 'Param Mitra'] },
  { activityId: 'business_agreement', label: 'Sign Business Agreement', primaryTaras: ['Sampat', 'Sadhaka', 'Mitra'], secondaryTaras: ['Kshema', 'Param Mitra'] },
  { activityId: 'purchase_property', label: 'Purchase Property', primaryTaras: ['Sampat', 'Kshema', 'Sadhaka'], secondaryTaras: ['Mitra', 'Param Mitra'] },
  { activityId: 'purchase_vehicle', label: 'Purchase Vehicle', primaryTaras: ['Sampat', 'Kshema', 'Mitra'], secondaryTaras: ['Sadhaka', 'Param Mitra'] },
  { activityId: 'major_investment', label: 'Make Major Investment', primaryTaras: ['Sampat', 'Sadhaka', 'Param Mitra'], secondaryTaras: ['Kshema', 'Mitra'] },
  { activityId: 'start_education', label: 'Start Education/Course', primaryTaras: ['Sadhaka', 'Kshema', 'Mitra'], secondaryTaras: ['Param Mitra', 'Sampat'] },
  { activityId: 'start_project', label: 'Start Important Project', primaryTaras: ['Sampat', 'Sadhaka', 'Kshema'], secondaryTaras: ['Mitra', 'Param Mitra'] },
  { activityId: 'travel', label: 'Travel', primaryTaras: ['Kshema', 'Sadhaka', 'Mitra'], secondaryTaras: ['Sampat', 'Param Mitra'] },
  { activityId: 'purchase_gemstone', label: 'Purchase Gemstone', primaryTaras: ['Sampat', 'Sadhaka', 'Param Mitra'], secondaryTaras: ['Kshema', 'Mitra'] },
  { activityId: 'ruby_manik', label: 'Wear Ruby (Manik)', primaryTaras: ['Sampat', 'Sadhaka', 'Param Mitra'], secondaryTaras: ['Kshema', 'Mitra'] },
  { activityId: 'pearl_moti', label: 'Wear Pearl (Moti)', primaryTaras: ['Sampat', 'Kshema', 'Mitra'], secondaryTaras: ['Sadhaka', 'Param Mitra'] },
  { activityId: 'coral_moonga', label: 'Wear Red Coral (Moonga)', primaryTaras: ['Sampat', 'Sadhaka', 'Kshema'], secondaryTaras: ['Mitra', 'Param Mitra'] },
  { activityId: 'emerald_panna', label: 'Wear Emerald (Panna)', primaryTaras: ['Sampat', 'Sadhaka', 'Param Mitra'], secondaryTaras: ['Kshema', 'Mitra'] },
  { activityId: 'yellow_sapphire_pukhraj', label: 'Wear Yellow Sapphire (Pukhraj)', primaryTaras: ['Sampat', 'Sadhaka', 'Param Mitra'], secondaryTaras: ['Kshema', 'Mitra'] },
  { activityId: 'diamond_opal_heera', label: 'Wear Diamond / Opal (Heera)', primaryTaras: ['Sampat', 'Kshema', 'Mitra'], secondaryTaras: ['Sadhaka', 'Param Mitra'] },
  { activityId: 'blue_sapphire_neelam', label: 'Wear Blue Sapphire (Neelam)', primaryTaras: ['Sadhaka', 'Kshema', 'Sampat'], secondaryTaras: ['Mitra', 'Param Mitra'] },
  { activityId: 'hessonite_gomed', label: 'Wear Hessonite (Gomed)', primaryTaras: ['Sadhaka', 'Sampat', 'Kshema'], secondaryTaras: ['Mitra', 'Param Mitra'] },
  { activityId: 'cats_eye_lehsunia', label: 'Wear Cats Eye (Lehsuniya)', primaryTaras: ['Sadhaka', 'Sampat', 'Mitra'], secondaryTaras: ['Kshema', 'Param Mitra'] },
  { activityId: 'marriage_activity', label: 'Marriage-related Activity', primaryTaras: ['Kshema', 'Mitra', 'Param Mitra'], secondaryTaras: ['Sampat', 'Sadhaka'] },
  { activityId: 'general_auspicious', label: 'General Auspicious Activity', primaryTaras: ['Sampat', 'Kshema', 'Sadhaka'], secondaryTaras: ['Mitra', 'Param Mitra'] }
];

// --- PANCHANGA / ASTRONOMICAL ENGINE ---

export interface DailyPanchangaFactors {
  location?: MuhurtaLocationContext;
  vara: string;
  rahuKalam: { start: string; end: string; rawStart: Date; rawEnd: Date };
  yamaganda: { start: string; end: string; rawStart: Date; rawEnd: Date };
  gulikaKalam: { start: string; end: string; rawStart: Date; rawEnd: Date };
  abhijit: { start: string; end: string; rawStart: Date; rawEnd: Date };
  sunrise: Date;
  sunset: Date;
  pitruPaksha?: PitruPakshaContext;
  vratObservance?: VratObservanceInfo;
  sankranti?: SankrantiInfo;
}

export interface TimePanchangaFactors {
  tithi: string;
  tithiNumber: number;
  nakshatra: string;
  moonNakIdx: number;
  nakshatraPada: number;
  nakshatraType?: NakshatraTypeInfo;
  yoga: string;
  karana: string;
  moonRashi: ZodiacSign;
  hora: string;
  choghadiya?: ChoghadiyaResult;
}

export function getDailyPanchangaFactors(
  date: Date,
  lat: number,
  lon: number,
  options?: { timezoneOffsetMinutes?: number; elevation?: number; placeName?: string }
): DailyPanchangaFactors {
  if (typeof lat !== 'number' || typeof lon !== 'number' || isNaN(lat) || isNaN(lon)) {
    throw new Error(
      "LocationCoordinatesInvalid: Valid latitude and longitude are strictly required for getDailyPanchangaFactors."
    );
  }
  const auth = calculateAuthoritativePanchanga(date, {
    latitude: lat,
    longitude: lon,
    timezoneOffsetMinutes: options?.timezoneOffsetMinutes,
    elevationMeters: options?.elevation
  });
  return {
    location: {
      latitude: lat,
      longitude: lon,
      timezoneOffsetMinutes: options?.timezoneOffsetMinutes,
      elevationMeters: options?.elevation,
      placeName: options?.placeName
    },
    vara: auth.vara.nameEn,
    rahuKalam: {
      start: auth.rahuKalam.start,
      end: auth.rahuKalam.end,
      rawStart: auth.rahuKalam.rawStart,
      rawEnd: auth.rahuKalam.rawEnd
    },
    yamaganda: {
      start: auth.yamaganda.start,
      end: auth.yamaganda.end,
      rawStart: auth.yamaganda.rawStart,
      rawEnd: auth.yamaganda.rawEnd
    },
    gulikaKalam: {
      start: auth.gulikaKalam.start,
      end: auth.gulikaKalam.end,
      rawStart: auth.gulikaKalam.rawStart,
      rawEnd: auth.gulikaKalam.rawEnd
    },
    abhijit: {
      start: auth.abhijit.start,
      end: auth.abhijit.end,
      rawStart: auth.abhijit.rawStart,
      rawEnd: auth.abhijit.rawEnd
    },
    sunrise: auth.sunrise,
    sunset: auth.sunset,
    pitruPaksha: auth.pitruPaksha,
    vratObservance: auth.vratObservance,
    sankranti: auth.sankranti
  };
}

export interface ChoghadiyaResult {
  name: 'Amrit' | 'Shubh' | 'Labh' | 'Char' | 'Rog' | 'Kaal' | 'Udveg';
  hiName: string;
  quality: 'Auspicious' | 'Favorable' | 'Inauspicious';
  ruler: PlanetName;
  isAuspicious: boolean;
}

export function getChoghadiyaForTime(
  date: Date,
  sunrise: Date,
  sunset: Date,
  vara: string
): ChoghadiyaResult {
  const VARA_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayIdx = Math.max(0, VARA_NAMES.indexOf(vara));
  const factor = calculateCanonicalChoghadiya(date, sunrise, sunset, dayIdx);
  return {
    name: factor.name,
    hiName: factor.hiName,
    quality: factor.quality,
    ruler: factor.ruler as PlanetName,
    isAuspicious: factor.isAuspicious
  };
}

export function getTimePanchangaFactors(date: Date, dailyFactors: DailyPanchangaFactors): TimePanchangaFactors {
  if (
    !dailyFactors?.location ||
    typeof dailyFactors.location.latitude !== 'number' ||
    typeof dailyFactors.location.longitude !== 'number' ||
    isNaN(dailyFactors.location.latitude) ||
    isNaN(dailyFactors.location.longitude)
  ) {
    throw new Error(
      "LocationContextMissing: Authoritative latitude and longitude are strictly required in DailyPanchangaFactors for location-sensitive Muhurta evaluation. Silent fallback to default coordinates is prohibited."
    );
  }

  const auth = calculateAuthoritativePanchanga(date, {
    latitude: dailyFactors.location.latitude,
    longitude: dailyFactors.location.longitude,
    timezoneOffsetMinutes: dailyFactors.location.timezoneOffsetMinutes,
    elevationMeters: dailyFactors.location.elevationMeters
  });

  const moonSignIdx = Math.floor(auth.ephemeris.moonLongitude / 30);
  const moonRashi = ZODIAC_SIGNS[moonSignIdx];
  const nakshatraType = getNakshatraType(auth.nakshatra.index);

  return {
    tithi: auth.tithi.name,
    tithiNumber: auth.tithi.number,
    nakshatra: auth.nakshatra.name,
    moonNakIdx: auth.nakshatra.index,
    nakshatraPada: auth.nakshatra.pada,
    nakshatraType,
    yoga: auth.yoga.name,
    karana: auth.karana.name,
    moonRashi,
    hora: auth.hora.ruler,
    choghadiya: {
      name: auth.choghadiya.name,
      hiName: auth.choghadiya.hiName,
      quality: auth.choghadiya.quality,
      ruler: auth.choghadiya.ruler,
      isAuspicious: auth.choghadiya.isAuspicious
    }
  };
}

export function getPanchangaForTime(
  date: Date,
  lat: number,
  lon: number,
  options?: { timezoneOffsetMinutes?: number; elevation?: number }
) {
  if (typeof lat !== 'number' || typeof lon !== 'number' || isNaN(lat) || isNaN(lon)) {
    throw new Error(
      "LocationCoordinatesInvalid: Valid latitude and longitude are strictly required for getPanchangaForTime."
    );
  }
  const auth = calculateAuthoritativePanchanga(date, {
    latitude: lat,
    longitude: lon,
    timezoneOffsetMinutes: options?.timezoneOffsetMinutes,
    elevationMeters: options?.elevation
  });
  const moonSignIdx = Math.floor(auth.ephemeris.moonLongitude / 30);
  const moonRashi = ZODIAC_SIGNS[moonSignIdx];
  const nakshatraType = getNakshatraType(auth.nakshatra.index);

  return {
    vara: auth.vara.nameEn,
    rahuKalam: {
      start: auth.rahuKalam.start,
      end: auth.rahuKalam.end,
      rawStart: auth.rahuKalam.rawStart,
      rawEnd: auth.rahuKalam.rawEnd
    },
    yamaganda: {
      start: auth.yamaganda.start,
      end: auth.yamaganda.end,
      rawStart: auth.yamaganda.rawStart,
      rawEnd: auth.yamaganda.rawEnd
    },
    gulikaKalam: {
      start: auth.gulikaKalam.start,
      end: auth.gulikaKalam.end,
      rawStart: auth.gulikaKalam.rawStart,
      rawEnd: auth.gulikaKalam.rawEnd
    },
    abhijit: {
      start: auth.abhijit.start,
      end: auth.abhijit.end,
      rawStart: auth.abhijit.rawStart,
      rawEnd: auth.abhijit.rawEnd
    },
    sunrise: auth.sunrise,
    sunset: auth.sunset,
    tithi: auth.tithi.name,
    tithiNumber: auth.tithi.number,
    nakshatra: auth.nakshatra.name,
    moonNakIdx: auth.nakshatra.index,
    nakshatraPada: auth.nakshatra.pada,
    nakshatraType,
    yoga: auth.yoga.name,
    karana: auth.karana.name,
    moonRashi,
    hora: auth.hora.ruler,
    choghadiya: {
      name: auth.choghadiya.name,
      hiName: auth.choghadiya.hiName,
      quality: auth.choghadiya.quality,
      ruler: auth.choghadiya.ruler,
      isAuspicious: auth.choghadiya.isAuspicious
    }
  };
}

// --- VEDIC LAGNA & SHUDDHI ENGINE ---

export function calculateMuhurtaLagna(
  date: Date,
  lat: number,
  lon: number,
  shuddhiOptions?: VedicShuddhiOptions
): LagnaShuddhiDetails {
  if (typeof lat !== 'number' || typeof lon !== 'number' || isNaN(lat) || isNaN(lon)) {
    throw new Error(
      "LocationCoordinatesInvalid: Valid latitude and longitude are strictly required for calculateMuhurtaLagna."
    );
  }
  const time = Astronomy.MakeTime(date);
  const ayanamsha = getLahiriAyanamsha(time);
  const ascDeg = calculateAscendant(date, lat, lon, ayanamsha);
  const signIndex = Math.floor(ascDeg / 30);
  const sign = ZODIAC_SIGNS[signIndex];
  const degreeInSign = ascDeg % 30;

  // Modality:
  // Sthira (Fixed: Taurus 1, Leo 4, Scorpio 7, Aquarius 10)
  // Chara (Movable: Aries 0, Cancer 3, Libra 6, Capricorn 9)
  // Dwisvabhava (Dual: Gemini 2, Virgo 5, Sagittarius 8, Pisces 11)
  let modality: 'Sthira' | 'Chara' | 'Dwisvabhava' = 'Dwisvabhava';
  let modalityLabel = 'Dual • द्विस्वभाव';
  if ([1, 4, 7, 10].includes(signIndex)) {
    modality = 'Sthira';
    modalityLabel = 'Fixed • स्थिर (Permanence)';
  } else if ([0, 3, 6, 9].includes(signIndex)) {
    modality = 'Chara';
    modalityLabel = 'Movable • चर (Dynamic / Speed)';
  }

  // Live Planetary Ephemeris for this moment
  const planets = calculateLivePlanetaryEphemeris(date);

  const ashtamaPlanets: string[] = [];
  const upachayaMalefics: string[] = [];
  const kendraBenefics: string[] = [];
  const trikonaBenefics: string[] = [];

  const naturalMalefics = ['Sun', 'Mars', 'Saturn', 'Rahu', 'Ketu'];
  const naturalBenefics = ['Jupiter', 'Venus', 'Mercury'];

  planets.forEach(p => {
    const house = ((p.signIndex - signIndex + 12) % 12) + 1;
    if (house === 8) {
      ashtamaPlanets.push(p.planet);
    }
    if ([3, 6, 11].includes(house) && naturalMalefics.includes(p.planet)) {
      upachayaMalefics.push(`${p.planet} (${house}H)`);
    }
    if ([1, 4, 7, 10].includes(house) && naturalBenefics.includes(p.planet)) {
      kendraBenefics.push(`${p.planet} (${house}H)`);
    }
    if ([5, 9].includes(house) && naturalBenefics.includes(p.planet)) {
      trikonaBenefics.push(`${p.planet} (${house}H)`);
    }
  });

  const isAshtamaClean = ashtamaPlanets.length === 0;

  // Gandanta checks:
  // 1. Nakshatra Gandanta: Moon in Revati-Ashwini (356.66°-360° or 0°-3.33°), Ashlesha-Magha (116.66°-120° or 120°-123.33°), Jyeshtha-Mula (236.66°-240° or 240°-243.33°)
  const moon = planets.find(p => p.planet === 'Moon');
  let isGandanta = false;
  let gandantaType: 'Tithi' | 'Nakshatra' | 'Lagna' | undefined = undefined;

  if (moon) {
    const mLon = moon.longitude;
    if (
      (mLon >= 356.66 || mLon <= 3.33) ||
      (mLon >= 116.66 && mLon <= 123.33) ||
      (mLon >= 236.66 && mLon <= 243.33)
    ) {
      isGandanta = true;
      gandantaType = 'Nakshatra';
    }
  }

  // 2. Lagna Gandanta: First 1° of Aries, Leo, Sagittarius, or last 1° of Cancer, Scorpio, Pisces
  if (!isGandanta) {
    if (
      (ascDeg >= 119 && ascDeg <= 121) ||
      (ascDeg >= 239 && ascDeg <= 241) ||
      (ascDeg >= 359 || ascDeg <= 1)
    ) {
      isGandanta = true;
      gandantaType = 'Lagna';
    }
  }

  // Visha Ghati: Toxic 4-Ghati (96 min) interval in each Nakshatra
  const moonNakIdx = moon ? Math.floor(moon.longitude / (360 / 27)) : 0;
  const degreeInNak = moon ? (moon.longitude % (360 / 27)) : 0;
  const vishaStarts: number[] = [
    50, 24, 30, 40, 14, 21, 30, 20, 32, 30, 20, 18, 21, 20, 14, 14, 10, 56, 40, 20, 20, 20, 18, 20, 24, 24, 30
  ];
  const starGhati = vishaStarts[moonNakIdx] || 20;
  const vishaStartDeg = (starGhati / 60) * (360 / 27);
  const vishaEndDeg = vishaStartDeg + 0.888;
  const isVishaGhati = degreeInNak >= vishaStartDeg && degreeInNak <= vishaEndDeg;

  const passedRules: string[] = [];
  const breachedRules: string[] = [];

  if (isAshtamaClean) {
    passedRules.push('Ashtama Shuddhi (8H Clean)');
  } else {
    breachedRules.push(`Ashtama Dosha (${ashtamaPlanets.join(', ')})`);
  }

  if (modality === 'Sthira') {
    passedRules.push('Sthira (Fixed) Lagna');
  } else if (modality === 'Chara') {
    passedRules.push('Chara (Movable) Lagna');
  }

  if (upachayaMalefics.length > 0) {
    passedRules.push(`Upachaya Strength (${upachayaMalefics.length})`);
  }
  if (kendraBenefics.length > 0) {
    passedRules.push(`Kendra Benefics (${kendraBenefics.length})`);
  }

  if (isGandanta) {
    breachedRules.push(`${gandantaType} Gandanta`);
  }
  if (isVishaGhati) {
    breachedRules.push('Visha Ghati');
  }

  let shuddhiScore = 70;
  if (isAshtamaClean) shuddhiScore += 15; else shuddhiScore -= 25;
  if (upachayaMalefics.length > 0) shuddhiScore += 10;
  if (kendraBenefics.length > 0) shuddhiScore += 10;
  if (isGandanta) shuddhiScore -= 30;
  if (isVishaGhati) shuddhiScore -= 20;

  // Granular Classical Sub-Scores
  let modalityScore = 8;
  if (shuddhiOptions?.requireSthiraLagna) {
    modalityScore = modality === 'Sthira' ? 10 : 3;
  } else if (shuddhiOptions?.requireCharaLagna) {
    modalityScore = modality === 'Chara' ? 10 : 4;
  } else {
    modalityScore = modality === 'Sthira' ? 10 : (modality === 'Chara' ? 8 : 7);
  }

  const ashtamaScore = isAshtamaClean ? 8 : 0;
  const upachayaScore = upachayaMalefics.length >= 2 ? 6 : (upachayaMalefics.length === 1 ? 4 : 2);
  const kendraScore = kendraBenefics.length >= 2 ? 6 : (kendraBenefics.length === 1 ? 4 : 2);
  const penaltyDeductions = (isGandanta ? 10 : 0) + (isVishaGhati ? 8 : 0);
  const totalLagnaPoints = Math.max(0, modalityScore + ashtamaScore + upachayaScore + kendraScore - penaltyDeductions);

  const scoreBreakdown: LagnaScoreBreakdown = {
    modalityScore,
    maxModalityScore: 10,
    ashtamaScore,
    maxAshtamaScore: 8,
    upachayaScore,
    maxUpachayaScore: 6,
    kendraScore,
    maxKendraScore: 6,
    penaltyDeductions,
    totalScore: totalLagnaPoints,
    maxScore: 30
  };

  return {
    sign,
    signIndex,
    degreeInSign,
    formattedDegree: formatDegree(degreeInSign),
    modality,
    modalityLabel,
    isAshtamaClean,
    ashtamaPlanets,
    upachayaMalefics,
    kendraBenefics,
    trikonaBenefics,
    isGandanta,
    gandantaType,
    isVishaGhati,
    shuddhiScore: Math.max(0, Math.min(100, shuddhiScore)),
    scoreBreakdown,
    passedRules,
    breachedRules
  };
}

// --- AUSPICIOUS OVERRIDE YOGAS ---

export function detectAuspiciousYogas(panchanga: any, date: Date): AuspiciousYogaInfo[] {
  const yogas: AuspiciousYogaInfo[] = [];
  const rawNak = typeof panchanga.nakshatra === 'string' ? panchanga.nakshatra : (panchanga.nakshatra?.name || '');
  const nak = normalizeNakshatraName(rawNak);
  const vara = panchanga.vara;

  // 1. Guru-Pushya Yoga
  if (nak === 'Pushya' && vara === 'Thursday') {
    yogas.push({
      name: 'Guru-Pushya Amrita Yoga',
      hiName: 'गुरु-पुष्य अमृत योग',
      type: 'guru_pushya',
      description: 'Supreme royal yoga for business incorporation, buying gold, and signing agreements.',
      multiplierBenefit: 'Supreme Auspiciousness (राजाधिराज योग)'
    });
  }

  // 2. Ravi-Pushya Yoga
  if (nak === 'Pushya' && vara === 'Sunday') {
    yogas.push({
      name: 'Ravi-Pushya Amrita Yoga',
      hiName: 'रवि-पुष्य अमृत योग',
      type: 'ravi_pushya',
      description: 'Supreme solar blessing for leadership authority, administrative filings, and high-value acquisitions.',
      multiplierBenefit: 'Solar Crown Multiplier'
    });
  }

  // 3. Amrita Siddhi Yoga (Classical pairs normalized)
  const amritaPairs: Record<string, string[]> = {
    Sunday: ['Hasta'],
    Monday: ['Mrigashira'],
    Tuesday: ['Ashwini'],
    Wednesday: ['Anuradha'],
    Thursday: ['Pushya'],
    Friday: ['Revati'],
    Saturday: ['Rohini']
  };

  const amritaList = (amritaPairs[vara] || []).map(normalizeNakshatraName);
  if (amritaList.includes(nak) && !(nak === 'Pushya' && vara === 'Thursday')) {
    yogas.push({
      name: 'Amrita Siddhi Yoga',
      hiName: 'अमृत सिद्धि योग',
      type: 'amrita_siddhi',
      description: 'Classical immortality and compounding success yoga; nullifies common temporal imperfections.',
      multiplierBenefit: 'Enduring Success & Longevity'
    });
  }

  // 4. Sarvartha Siddhi Yoga (Classical pairs normalized)
  const sarvarthaPairs: Record<string, string[]> = {
    Sunday: ['Hasta', 'Pushya', 'Uttara Phalguni', 'Uttara Ashadha', 'Uttara Bhadrapada', 'Mula', 'Ashwini'],
    Monday: ['Rohini', 'Mrigashira', 'Pushya', 'Anuradha', 'Shravana'],
    Tuesday: ['Ashwini', 'Krittika'],
    Wednesday: ['Rohini', 'Mrigashira', 'Hasta', 'Anuradha', 'Krittika'],
    Thursday: ['Ashwini', 'Rohini', 'Pushya', 'Punarvasu', 'Anuradha'],
    Friday: ['Ashwini', 'Rohini', 'Revati', 'Anuradha'],
    Saturday: ['Rohini', 'Shravana', 'Shatabhisha']
  };

  const sarvarthaList = (sarvarthaPairs[vara] || []).map(normalizeNakshatraName);
  if (sarvarthaList.includes(nak) && !yogas.some(y => y.type === 'amrita_siddhi' || y.type === 'guru_pushya')) {
    yogas.push({
      name: 'Sarvartha Siddhi Yoga',
      hiName: 'सर्वार्थ सिद्धि योग',
      type: 'sarvartha_siddhi',
      description: 'Fulfills all endeavors (Sarva-Artha) and ensures auspicious manifestation of objectives.',
      multiplierBenefit: 'Universal Fruition Multiplier'
    });
  }

  // 5. Abhijit Muhurta
  if (panchanga.abhijit?.rawStart && panchanga.abhijit?.rawEnd) {
    const timeMs = date.getTime();
    if (timeMs >= panchanga.abhijit.rawStart.getTime() && timeMs <= panchanga.abhijit.rawEnd.getTime()) {
      // Classically prohibited/afflicted on Wednesday (Budhaditya/Rahu dosha)
      if (vara !== 'Wednesday') {
        yogas.push({
          name: 'Abhijit Muhurta Window',
          hiName: 'अभिजित मुहूर्त काल',
          type: 'abhijit',
          description: 'Midday 8th Muhurta ruled by Lord Vishnu; cleanses malefic afflictions and grants victory.',
          multiplierBenefit: 'Vishnu Protection Shield'
        });
      }
    }
  }

  return yogas;
}

// --- CLASSICAL YOGA & KARANA RULE ENGINES ---

export function evaluateYogaRule(
  yogaName: string,
  activityId?: string
): YogaRuleResult {
  const norm = (yogaName || '').trim().toLowerCase();
  const act = (activityId || '').toLowerCase();

  const isAuspiciousCeremony = [
    'marriage_activity', 'vivaha_marriage', 'griha_pravesh', 'housewarming',
    'upanayana', 'naming_ceremony', 'namakarana', 'property_purchase',
    'property_registration', 'bhumi_pujan', 'business_launch', 'general_auspicious'
  ].some(k => act.includes(k));

  const isTravel = act.includes('travel') || act.includes('yatra') || act.includes('journey');
  const isSurgery = act.includes('surgery') || act.includes('medical');
  const isHostileOrLitigation = [
    'legal_dispute', 'filing_lawsuit', 'court_case', 'dispute_resolution',
    'competitor_strategy', 'demolition', 'pest_control'
  ].some(k => act.includes(k));
  const isSpiritualOrPitru = [
    'spiritual_initiation', 'japa', 'mantra', 'pitru', 'tarpana', 'shradh'
  ].some(k => act.includes(k));
  const isCommercialOrInvestment = [
    'business_launch', 'financial_investment', 'contract_signing',
    'partnership', 'trade', 'job_interview', 'negotiation'
  ].some(k => act.includes(k));
  const isGemstone = act.includes('gemstone') || act.includes('manik') || act.includes('moti') ||
    act.includes('moonga') || act.includes('panna') || act.includes('pukhraj') ||
    act.includes('heera') || act.includes('neelam') || act.includes('gomed') || act.includes('lehsunia');

  // 1. Mahadoshas: Vyatipata & Vaidhriti
  if (norm.includes('vyatipata') || norm.includes('vyatipat')) {
    if (isSpiritualOrPitru) {
      return {
        yoga: yogaName,
        activityId: activityId || 'general',
        applicability: 'Spiritual / Japa / Pitru Karma',
        classification: 'FAVOURABLE',
        severity: 'none',
        source: 'Kalaprakasika & Brihat Samhita',
        explanation: 'Vyatipata possesses concentrated transcendental energy auspicious for Pitru Tarpana, Japa, and spiritual initiation.',
        scoreModifier: 6
      };
    }
    return {
      yoga: yogaName,
      activityId: activityId || 'general',
      applicability: 'All worldly, celebratory, marriage, and commercial undertakings',
      classification: 'HARD_EXCLUSION',
      severity: 'critical',
      source: 'Muhurta Chintamani (Prakarana 1) & Kalaprakasika',
      explanation: 'Vyatipata is a classical Mahadosha strictly prohibited for worldly initiatives, marriage, travel, and investments.',
      scoreModifier: -30
    };
  }

  if (norm.includes('vaidhriti') || norm.includes('vaidhrit')) {
    if (isSpiritualOrPitru) {
      return {
        yoga: yogaName,
        activityId: activityId || 'general',
        applicability: 'Spiritual contemplation',
        classification: 'NEUTRAL',
        severity: 'low',
        source: 'Classical Muhurta Tradition',
        explanation: 'Vaidhriti can be utilized for quiet spiritual reflection but remains unsuitable for celebratory initiation.',
        scoreModifier: 0
      };
    }
    return {
      yoga: yogaName,
      activityId: activityId || 'general',
      applicability: 'All worldly, commercial, marital, and residential milestones',
      classification: 'HARD_EXCLUSION',
      severity: 'critical',
      source: 'Muhurta Chintamani & Brihat Samhita',
      explanation: 'Vaidhriti is a premier Mahadosha signifying complete severance, prohibited for marriage, foundation laying, and journeys.',
      scoreModifier: -30
    };
  }

  // 2. Severe Inauspicious / Afflicted Yogas
  if (norm.includes('atiganda')) {
    if (isTravel || isSurgery || isAuspiciousCeremony) {
      return {
        yoga: yogaName,
        activityId: activityId || 'general',
        applicability: 'Travel, Surgery, and Major Ceremonies',
        classification: 'RESTRICTIVE',
        severity: 'high',
        source: 'Muhurta Chintamani (Prakarana 1)',
        explanation: 'Atiganda brings acute danger and obstacles; first 6 Ghatis prohibited. Classically prohibited for journeys, surgery, and marriage.',
        scoreModifier: -20
      };
    }
    return {
      yoga: yogaName,
      activityId: activityId || 'general',
      applicability: 'General endeavors',
      classification: 'CAUTION',
      severity: 'moderate',
      source: 'Muhurta Chintamani',
      explanation: 'Atiganda is an afflicted yoga carrying potential obstacles; requires cautionary mitigation (Parihara).',
      scoreModifier: -10
    };
  }

  if (norm.includes('shula') || norm.includes('soola')) {
    if (isTravel || isSurgery || isAuspiciousCeremony || isCommercialOrInvestment) {
      return {
        yoga: yogaName,
        activityId: activityId || 'general',
        applicability: 'Travel, Surgery, Commerce, and Construction',
        classification: 'RESTRICTIVE',
        severity: 'high',
        source: 'Muhurta Chintamani',
        explanation: 'Shula (spear/piercing pain) is classically prohibited for journeys, surgery, and foundational undertakings. First 5 Ghatis prohibited.',
        scoreModifier: -20
      };
    }
    return {
      yoga: yogaName,
      activityId: activityId || 'general',
      applicability: 'General undertakings',
      classification: 'CAUTION',
      severity: 'moderate',
      source: 'Muhurta Chintamani',
      explanation: 'Shula Yoga carries sharp malefic potential; observe strict caution.',
      scoreModifier: -10
    };
  }

  if (norm.includes('ganda')) {
    if (isTravel || isCommercialOrInvestment || isAuspiciousCeremony) {
      return {
        yoga: yogaName,
        activityId: activityId || 'general',
        applicability: 'Initiations, Travel, and Commerce',
        classification: 'RESTRICTIVE',
        severity: 'high',
        source: 'Muhurta Chintamani',
        explanation: 'Ganda (knot/bottleneck) creates entanglements and stalls progress; first 6 Ghatis prohibited.',
        scoreModifier: -18
      };
    }
    return {
      yoga: yogaName,
      activityId: activityId || 'general',
      applicability: 'General activities',
      classification: 'CAUTION',
      severity: 'moderate',
      source: 'Muhurta Chintamani',
      explanation: 'Ganda Yoga indicates knotty obstacles; careful supervision required.',
      scoreModifier: -8
    };
  }

  if (norm.includes('vyaghata') || norm.includes('vyaghat')) {
    if (isHostileOrLitigation) {
      return {
        yoga: yogaName,
        activityId: activityId || 'general',
        applicability: 'Litigation, Demolition, and Martial Operations',
        classification: 'FAVOURABLE',
        severity: 'none',
        source: 'Muhurta Chintamani',
        explanation: 'Vyaghata possesses fierce/destructive energy favourable for overpowering adversaries, litigation, and demolition.',
        scoreModifier: 5
      };
    }
    return {
      yoga: yogaName,
      activityId: activityId || 'general',
      applicability: 'Peaceful, marital, and celebratory undertakings',
      classification: 'RESTRICTIVE',
      severity: 'high',
      source: 'Muhurta Chintamani',
      explanation: 'Vyaghata (violent strike) is prohibited for peaceful ceremonies, travel, partnerships, and weddings.',
      scoreModifier: -18
    };
  }

  if (norm.includes('vajra')) {
    if (isAuspiciousCeremony || isGemstone || isSurgery) {
      return {
        yoga: yogaName,
        activityId: activityId || 'general',
        applicability: 'Delicate ceremonies, Gemstones, and Healing',
        classification: 'RESTRICTIVE',
        severity: 'high',
        source: 'Muhurta Chintamani',
        explanation: 'Vajra (thunderbolt) is harsh and unyielding; first 3 Ghatis prohibited. Avoid for delicate ceremonies and healing.',
        scoreModifier: -15
      };
    }
    return {
      yoga: yogaName,
      activityId: activityId || 'general',
      applicability: 'Hard engineering and mechanical works',
      classification: 'NEUTRAL',
      severity: 'low',
      source: 'Muhurta Ganapati',
      explanation: 'Vajra Yoga hardness is tolerable for durable machinery and metallurgical construction.',
      scoreModifier: 0
    };
  }

  if (norm.includes('parigha')) {
    if (isTravel) {
      return {
        yoga: yogaName,
        activityId: activityId || 'general',
        applicability: 'Travel and Journeys',
        classification: 'RESTRICTIVE',
        severity: 'high',
        source: 'Muhurta Chintamani',
        explanation: 'Parigha (iron bar/obstruction) bars doorways and transit routes; first 30 Ghatis prohibited for departure.',
        scoreModifier: -18
      };
    }
    return {
      yoga: yogaName,
      activityId: activityId || 'general',
      applicability: 'General endeavors',
      classification: 'CAUTION',
      severity: 'moderate',
      source: 'Muhurta Chintamani',
      explanation: 'Parigha carries obstruction energy; examine subsequent temporal limbs.',
      scoreModifier: -8
    };
  }

  if (norm.includes('vishkambha') || norm.includes('vishkambh')) {
    return {
      yoga: yogaName,
      activityId: activityId || 'general',
      applicability: 'General undertakings',
      classification: 'CAUTION',
      severity: 'low',
      source: 'Muhurta Chintamani',
      explanation: 'Vishkambha represents an obstacle or pillar; the first 3 Ghatis (approx 72 min) are prohibited.',
      scoreModifier: -6
    };
  }

  // 3. Benefic Auspicious Nitya Yogas
  const beneficYogas = [
    'priti', 'preeti', 'ayushman', 'saubhagya', 'shobhana', 'sukarma',
    'dhriti', 'vriddhi', 'dhruva', 'harshana', 'harshan', 'siddhi',
    'variyan', 'shiva', 'siddha', 'sadhya', 'shubha', 'shukla',
    'brahma', 'indra'
  ];

  if (beneficYogas.some(by => norm.includes(by))) {
    return {
      yoga: yogaName,
      activityId: activityId || 'general',
      applicability: 'All constructive, creative, commercial, and celebratory initiatives',
      classification: 'FAVOURABLE',
      severity: 'none',
      source: 'Muhurta Chintamani (Prakarana 1)',
      explanation: `${yogaName} is a classical benefic Nitya Yoga, generating positive momentum and compounding auspicious results.`,
      scoreModifier: 6
    };
  }

  // 4. Unverified / Unknown Yoga
  return {
    yoga: yogaName,
    activityId: activityId || 'general',
    applicability: 'Unspecified',
    classification: 'UNKNOWN',
    severity: 'none',
    source: 'Unverified / Acharya Pause',
    explanation: `No verified classical activity rule for ${yogaName} Yoga. Proceeding with neutral epistemic evaluation.`,
    scoreModifier: 0
  };
}

export function evaluateKaranaRule(
  karanaName: string,
  isVishtiBhadra: boolean,
  activityId?: string
): KaranaRuleResult {
  const norm = (karanaName || '').trim().toLowerCase();
  const act = (activityId || '').toLowerCase();

  const isBhadra = isVishtiBhadra || norm.includes('vishti') || norm.includes('bhadra');

  const isHostileOrLitigation = [
    'legal_dispute', 'filing_lawsuit', 'court_case', 'dispute_resolution',
    'competitor_strategy', 'demolition', 'pest_control'
  ].some(k => act.includes(k));

  const isSurgery = act.includes('surgery') || act.includes('medical');

  // 1. Vishti / Bhadra Karana Handling
  if (isBhadra) {
    if (isHostileOrLitigation) {
      return {
        karana: karanaName || 'Vishti (Bhadra)',
        isVishtiBhadra: true,
        activityId: activityId || 'general',
        applicability: 'Litigation, Legal filing, Hostile confrontation, and Demolition',
        classification: 'FAVOURABLE',
        severity: 'none',
        source: 'Muhurta Chintamani (Prakarana 1, Shloka 48)',
        explanation: 'Bhadra (Vishti) Karana is classically declared auspicious for legal litigation, confrontation, hostile subdual, and defensive actions.',
        scoreModifier: 6
      };
    }
    if (isSurgery) {
      return {
        karana: karanaName || 'Vishti (Bhadra)',
        isVishtiBhadra: true,
        activityId: activityId || 'general',
        applicability: 'Invasive surgery and incision (Chedana/Bhedana)',
        classification: 'CAUTION',
        severity: 'moderate',
        source: 'Muhurta Chintamani & Sushruta Tradition',
        explanation: 'Classical texts permit invasive cutting and surgery in Bhadra, but elective procedures prefer gentle Karanas.',
        scoreModifier: -5
      };
    }
    return {
      karana: karanaName || 'Vishti (Bhadra)',
      isVishtiBhadra: true,
      activityId: activityId || 'general',
      applicability: 'All auspicious, marital, residential, commercial, travel, and milestone undertakings',
      classification: 'HARD_EXCLUSION',
      severity: 'critical',
      source: 'Muhurta Chintamani (Prakarana 1, Shloka 46)',
      explanation: 'Vishti (Bhadra) Karana is strictly prohibited for auspicious beginnings, marriage, housewarming, trade, and travel under classical Muhurta Shastra.',
      scoreModifier: -30
    };
  }

  // 2. Fixed Karanas: Shakuni, Chatushpada, Naga, Kintughna
  if (norm.includes('shakuni')) {
    if (act.includes('medicine') || act.includes('mantra') || act.includes('remedy')) {
      return {
        karana: karanaName,
        isVishtiBhadra: false,
        activityId: activityId || 'general',
        applicability: 'Medicine administration, Herb gathering, and Mantra siddhi',
        classification: 'FAVOURABLE',
        severity: 'none',
        source: 'Kalaprakasika',
        explanation: 'Shakuni Karana is auspicious for medicine preparation, mantra sadhana, and overcoming illness.',
        scoreModifier: 4
      };
    }
    return {
      karana: karanaName,
      isVishtiBhadra: false,
      activityId: activityId || 'general',
      applicability: 'Auspicious worldly beginnings and ceremonies',
      classification: 'RESTRICTIVE',
      severity: 'high',
      source: 'Muhurta Chintamani',
      explanation: 'Shakuni is a fixed inauspicious Karana; avoid for marriage, journeys, and commercial launch.',
      scoreModifier: -15
    };
  }

  if (norm.includes('chatushpada') || norm.includes('catuspada')) {
    if (act.includes('pitru') || act.includes('tarpana') || act.includes('shradh') || act.includes('animal') || act.includes('dairy')) {
      return {
        karana: karanaName,
        isVishtiBhadra: false,
        activityId: activityId || 'general',
        applicability: 'Pitru karma, Brahmin charity, and Animal care',
        classification: 'FAVOURABLE',
        severity: 'none',
        source: 'Muhurta Chintamani',
        explanation: 'Chatushpada Karana is auspicious for ancestral rites, charity, and veterinary endeavors.',
        scoreModifier: 4
      };
    }
    return {
      karana: karanaName,
      isVishtiBhadra: false,
      activityId: activityId || 'general',
      applicability: 'Worldly celebrations and ventures',
      classification: 'RESTRICTIVE',
      severity: 'high',
      source: 'Muhurta Chintamani',
      explanation: 'Chatushpada is avoided for festive ceremonies, marriage, and trade inaugurations.',
      scoreModifier: -15
    };
  }

  if (norm.includes('naga')) {
    if (act.includes('mining') || act.includes('underground') || act.includes('toxic') || act.includes('poison')) {
      return {
        karana: karanaName,
        isVishtiBhadra: false,
        activityId: activityId || 'general',
        applicability: 'Mining, Excavation, and Anti-toxin operations',
        classification: 'FAVOURABLE',
        severity: 'none',
        source: 'Brihat Samhita',
        explanation: 'Naga Karana is suited for excavation, mining, and dealing with subterranean elements.',
        scoreModifier: 4
      };
    }
    return {
      karana: karanaName,
      isVishtiBhadra: false,
      activityId: activityId || 'general',
      applicability: 'Worldly and marital ceremonies',
      classification: 'RESTRICTIVE',
      severity: 'high',
      source: 'Muhurta Chintamani',
      explanation: 'Naga Karana has venomous/destructive tendencies; avoid for partnerships, weddings, and travel.',
      scoreModifier: -15
    };
  }

  if (norm.includes('kintughna')) {
    return {
      karana: karanaName,
      isVishtiBhadra: false,
      activityId: activityId || 'general',
      applicability: 'Religious rituals, Vows, and Educational beginnings',
      classification: 'FAVOURABLE',
      severity: 'none',
      source: 'Brihat Samhita',
      explanation: 'Kintughna (destroyer of evils) is auspicious for religious offerings, havan, and educational initiation.',
      scoreModifier: 5
    };
  }

  // 3. Movable Benefic Karanas (Bava, Balava, Kaulava, Taitila, Garaja, Vanija)
  const movableBenefics = ['bava', 'balava', 'kaulava', 'taitila', 'garaja', 'vanija'];
  if (movableBenefics.some(mb => norm.includes(mb))) {
    return {
      karana: karanaName,
      isVishtiBhadra: false,
      activityId: activityId || 'general',
      applicability: 'General worldly, commercial, marital, and developmental undertakings',
      classification: 'FAVOURABLE',
      severity: 'none',
      source: 'Muhurta Chintamani (Prakarana 1)',
      explanation: `${karanaName} is an auspicious movable Karana (Shubha Karana) supporting fruitful progress and success.`,
      scoreModifier: 5
    };
  }

  // 4. Unknown Karana
  return {
    karana: karanaName,
    isVishtiBhadra: false,
    activityId: activityId || 'general',
    applicability: 'Unspecified',
    classification: 'UNKNOWN',
    severity: 'none',
    source: 'Unverified / Acharya Pause',
    explanation: `No verified classical activity rule for ${karanaName} Karana. Neutral evaluation applied.`,
    scoreModifier: 0
  };
}

// --- MUHURTA SCORING ENGINE ---

export function calculateMuhurtaScore(
  panchanga: any,
  taraBala: TaraBalaResult,
  activity: ActivityPreference,
  date: Date,
  janmaNakIdxs?: { name: string; idx: number }[],
  customRules?: any,
  framework: 'combo' | 'nakshatra' | 'rashi' = 'combo',
  janmaRashiIdxs?: { name: string; idx: number }[],
  shuddhiOptions?: VedicShuddhiOptions,
  lagnaDetails?: LagnaShuddhiDetails,
  specialYogas?: AuspiciousYogaInfo[]
): MuhurtaScore {
  const reasons: string[] = [];
  const cautions: string[] = [];

  // Pitru Paksha contextual evaluation (Activity-Specific)
  if (panchanga?.pitruPaksha?.isActive) {
    const actLabel = (activity?.label || '').toLowerCase();
    const actId = (activity?.activityId || '').toLowerCase();
    const isShraddhaOrPitru = actLabel.includes('shraddha') || actLabel.includes('pitru') || actLabel.includes('tarpana') || actId.includes('pitru');
    const isJoyousSamskara = actLabel.includes('marriage') || actLabel.includes('vivaha') || actLabel.includes('griha') || actLabel.includes('housewarming') || actLabel.includes('inauguration') || actLabel.includes('naming') || actId.includes('vivaha');
    const shraddhaName = panchanga.pitruPaksha.shraddhaTithiNameEn || 'Mahalaya';

    if (isShraddhaOrPitru) {
      reasons.push(`✓ Favourable timing: Active Pitru Paksha (${shraddhaName}) provides positive classical alignment for ancestral Shraddha and Pitru rituals.`);
    } else if (isJoyousSamskara) {
      cautions.push(`Caution: Traditional Muhurta practice treats joyous Samskaras and major new ventures with caution during Pitru Paksha (${shraddhaName}) under regional observance rules.`);
    } else {
      reasons.push(`• Neutral timing during Pitru Paksha (${shraddhaName}); routine worship and spiritual practices proceed normally.`);
    }
  }

  // Surya Sankranti & Punya Kaal contextual evaluation (Activity-Specific)
  if (panchanga?.sankranti?.isSankrantiDay) {
    const actLabel = (activity?.label || '').toLowerCase();
    const actId = (activity?.activityId || '').toLowerCase();
    const sankrantiName = panchanga.sankranti.sankrantiNameEn || 'Surya Sankranti';
    const isSpiritualOrCharity =
      actLabel.includes('spiritual') ||
      actLabel.includes('meditation') ||
      actLabel.includes('sadhana') ||
      actLabel.includes('puja') ||
      actLabel.includes('worship') ||
      actLabel.includes('charity') ||
      actLabel.includes('daan') ||
      actLabel.includes('mantra') ||
      actLabel.includes('snana') ||
      actLabel.includes('tarpana') ||
      actLabel.includes('gayatri') ||
      actId.includes('spiritual') ||
      actId.includes('puja');

    const isJoyousMaterialSamskara =
      actLabel.includes('marriage') ||
      actLabel.includes('vivaha') ||
      actLabel.includes('griha') ||
      actLabel.includes('housewarming') ||
      actLabel.includes('inauguration') ||
      actLabel.includes('naming') ||
      actId.includes('vivaha');

    if (isSpiritualOrCharity) {
      reasons.push(`✓ Highly auspicious: Active Punya Kaal for ${sankrantiName} provides elevated spiritual potency for charity, snana, and mantra sadhana.`);
    } else if (isJoyousMaterialSamskara) {
      cautions.push(`Caution: Classical Muhurta texts advise performing spiritual, charitable, and solar rites during ${sankrantiName} Punya Kaal rather than major material worldly beginnings.`);
    } else {
      reasons.push(`• Punya Kaal active for ${sankrantiName}; auspicious for charitable donations and solar prayers.`);
    }
  }

  // 1. TARA BALA EVALUATION (Vary weights based on framework)
  let taraScore = 15;
  const primTaras = customRules?.primaryTaras || activity?.primaryTaras || ['Sampat', 'Sadhaka', 'Kshema'];
  const secTaras = customRules?.secondaryTaras || activity?.secondaryTaras || ['Mitra', 'Param Mitra'];

  const maxTaraWeight = framework === 'nakshatra' ? 40 : (framework === 'combo' ? 25 : 10);

  if (janmaNakIdxs && janmaNakIdxs.length > 0) {
    let totalTaraScoreSum = 0;
    let worstSuitability: 'Excellent' | 'Good' | 'Neutral' | 'Poor' | 'Danger' = 'Excellent';

    janmaNakIdxs.forEach(person => {
      const personTara = calculateTaraBala(person.idx, panchanga.moonNakIdx);
      if (primTaras.includes(personTara.taraName)) {
        totalTaraScoreSum += maxTaraWeight;
      } else if (secTaras.includes(personTara.taraName)) {
        totalTaraScoreSum += Math.round(maxTaraWeight * 0.72);
      } else if (['Vipat', 'Pratyari', 'Naidhana'].includes(personTara.taraName)) {
        totalTaraScoreSum += 0;
        if (personTara.taraName === 'Naidhana') worstSuitability = 'Danger';
        else if (worstSuitability !== 'Danger') worstSuitability = 'Poor';
        cautions.push(`${person.name}: Unfavorable ${personTara.taraName} Tara.`);
      } else {
        totalTaraScoreSum += Math.round(maxTaraWeight * 0.4);
        if (worstSuitability !== 'Danger' && worstSuitability !== 'Poor') worstSuitability = 'Neutral';
      }
    });

    taraScore = Math.round(totalTaraScoreSum / janmaNakIdxs.length);
    if (worstSuitability === 'Excellent' || worstSuitability === 'Good') {
      reasons.push(`✓ High Tara Bala alignment (${framework === 'nakshatra' ? 'Primary Focus' : 'Verified'}).`);
    } else if (worstSuitability === 'Neutral') {
      reasons.push(`• Neutral Tara compatibility.`);
    }
  } else {
    // Single person / General
    if (primTaras.includes(taraBala.taraName)) {
      taraScore = maxTaraWeight;
      reasons.push(`✓ Favorable ${taraBala.taraName} Tara.`);
    } else if (secTaras.includes(taraBala.taraName)) {
      taraScore = Math.round(maxTaraWeight * 0.72);
      reasons.push(`✓ Supportive ${taraBala.taraName} Tara.`);
    } else if (['Vipat', 'Pratyari', 'Naidhana'].includes(taraBala.taraName)) {
      taraScore = 0;
      cautions.push(`Unfavorable ${taraBala.taraName} Tara.`);
    } else {
      taraScore = Math.round(maxTaraWeight * 0.4);
      reasons.push(`• Neutral ${taraBala.taraName} Tara.`);
    }
  }

  // 2. TITHI SHUDDHI
  const maxTithiWeight = framework === 'rashi' ? 20 : (framework === 'combo' ? 15 : 10);
  let tithiScore = Math.round(maxTithiWeight * 0.65);
  const preferredTithis = customRules?.preferredTithis;
  const restrictedTithis = [4, 9, 14, 30];

  if (preferredTithis) {
    if (preferredTithis.includes(panchanga.tithiNumber)) {
      tithiScore = maxTithiWeight;
      reasons.push(`✓ Preferred Tithi (${panchanga.tithiNumber}).`);
    } else if (restrictedTithis.includes(panchanga.tithiNumber)) {
      tithiScore = Math.round(maxTithiWeight * 0.2);
      cautions.push(`Rikta/Amavasya Tithi (${panchanga.tithiNumber}) is less auspicious.`);
    } else {
      tithiScore = Math.round(maxTithiWeight * 0.65);
      reasons.push(`• Neutral Tithi (${panchanga.tithiNumber}).`);
    }
  } else {
    if (restrictedTithis.includes(panchanga.tithiNumber)) {
      tithiScore = Math.round(maxTithiWeight * 0.2);
      cautions.push(`Tithi ${panchanga.tithiNumber} is less auspicious.`);
    } else {
      tithiScore = maxTithiWeight;
      reasons.push(`✓ Auspicious Tithi (${panchanga.tithiNumber}).`);
    }
  }

  // 3. NAKSHATRA SHUDDHI & NATURE
  const maxNakWeight = framework === 'nakshatra' ? 25 : (framework === 'combo' ? 15 : 10);
  let nakScore = Math.round(maxNakWeight * 0.65);
  const preferredNakshatras = customRules?.preferredNakshatras;
  const unstableNakshatras = ['Ardra', 'Ashlesha', 'Jyeshtha', 'Mula'].map(normalizeNakshatraName);
  const currentNormNak = normalizeNakshatraName(panchanga.nakshatra);

  if (preferredNakshatras) {
    const isPreferred = preferredNakshatras.some((p: string) => normalizeNakshatraName(p) === currentNormNak);
    if (isPreferred) {
      nakScore = maxNakWeight;
      reasons.push(`✓ Preferred Activity Nakshatra (${panchanga.nakshatra}).`);
    } else if (unstableNakshatras.includes(currentNormNak)) {
      nakScore = Math.round(maxNakWeight * 0.25);
      cautions.push(`Sharp/Fierce Nakshatra (${panchanga.nakshatra}) active.`);
    } else {
      nakScore = Math.round(maxNakWeight * 0.65);
      reasons.push(`• Neutral Nakshatra (${panchanga.nakshatra}).`);
    }
  } else {
    if (unstableNakshatras.includes(currentNormNak)) {
      nakScore = Math.round(maxNakWeight * 0.25);
      cautions.push(`Sharp Nakshatra (${panchanga.nakshatra}) active.`);
    } else {
      nakScore = maxNakWeight;
      reasons.push(`✓ Supportive Nakshatra (${panchanga.nakshatra}).`);
    }
  }

  // 4. CHANDRA RASHI & GOCHAR ALIGNMENT (Major in Rashi mode, Balanced in Combo, Baseline in Nakshatra)
  const maxRashiWeight = framework === 'rashi' ? 35 : (framework === 'combo' ? 20 : 8);
  let rashiScore = Math.round(maxRashiWeight * 0.6);
  const transitRashiIdx = ZODIAC_SIGNS.indexOf(panchanga.moonRashi);

  if (janmaRashiIdxs && janmaRashiIdxs.length > 0 && transitRashiIdx >= 0) {
    let totalRashiSum = 0;
    janmaRashiIdxs.forEach(person => {
      // Chandra Gochar house from Janma Rashi (1-indexed: 1 to 12)
      const gocharHouse = ((transitRashiIdx - person.idx + 12) % 12) + 1;
      // Auspicious Chandra Gochar houses: 1, 3, 6, 7, 10, 11 (Vedic standard)
      // Inauspicious (Chandra Dosha): 4, 8, 12 (Ashtama / Kantaka Chandra)
      if ([1, 3, 6, 7, 10, 11].includes(gocharHouse)) {
        totalRashiSum += maxRashiWeight;
        if (framework === 'rashi' || framework === 'combo') {
          reasons.push(`✓ Benefic Chandra Gochar (${gocharHouse}th from ${person.name}'s Moon Sign).`);
        }
      } else if ([4, 8, 12].includes(gocharHouse)) {
        totalRashiSum += Math.round(maxRashiWeight * 0.15);
        cautions.push(`Inauspicious Chandra Gochar (${gocharHouse}th from ${person.name}'s Moon Sign - Chandra Shuddhi required).`);
      } else {
        totalRashiSum += Math.round(maxRashiWeight * 0.6);
        reasons.push(`• Neutral Chandra Gochar (${gocharHouse}th from ${person.name}'s Moon Sign).`);
      }
    });
    rashiScore = Math.round(totalRashiSum / janmaRashiIdxs.length);
  } else {
    // General Chandra strength (Exaltation / Own Sign / Mitrashad)
    if (['Taurus', 'Cancer'].includes(panchanga.moonRashi)) {
      rashiScore = maxRashiWeight;
      reasons.push(`✓ Moon exalted/in own sign (${panchanga.moonRashi}).`);
    } else if (['Scorpio'].includes(panchanga.moonRashi)) {
      rashiScore = Math.round(maxRashiWeight * 0.2);
      cautions.push(`Moon debilitated in Scorpio.`);
    } else {
      rashiScore = Math.round(maxRashiWeight * 0.7);
    }
  }

  // 5. VARA & PROHIBITED PERIODS (Rahu / Yamaganda)
  let varaScore = 8;
  const avoidedVaras = customRules?.avoidedVaras || ['Tuesday', 'Saturday'];
  const isExcludedVaraActivity = ['travel', 'marriage_activity'].includes(activity?.activityId);

  if (avoidedVaras.includes(panchanga.vara) && !isExcludedVaraActivity) {
    varaScore = 3;
    cautions.push(`Avoided on ${panchanga.vara}.`);
  } else {
    reasons.push(`✓ Favorable Weekday (${panchanga.vara}).`);
  }

  let prohibitedScore = 8;
  const nowMs = date.getTime();
  if (nowMs >= panchanga.rahuKalam.rawStart.getTime() && nowMs <= panchanga.rahuKalam.rawEnd.getTime()) {
    prohibitedScore = 0;
    cautions.push(`In Rahu Kalam.`);
  } else if (nowMs >= panchanga.yamaganda.rawStart.getTime() && nowMs <= panchanga.yamaganda.rawEnd.getTime()) {
    prohibitedScore = 0;
    cautions.push(`In Yamaganda.`);
  } else {
    reasons.push(`✓ No Rahu/Yamaganda overlap.`);
  }

  let horaScore = 6;
  const beneficHoras = ['Jupiter', 'Venus', 'Mercury', 'Moon'];
  if (beneficHoras.includes(panchanga.hora)) {
    horaScore = 6;
    reasons.push(`✓ Benefic ${panchanga.hora} Hora.`);
  } else {
    horaScore = 2;
  }

  // 6. VEDIC SHUDDHI & SPECIAL YOGAS EVALUATION
  let shuddhiScore = 14;
  const maxShuddhiWeight = 25;
  let shuddhiBonus = 0;
  let shuddhiPenalty = 0;

  if (lagnaDetails) {
    // Sthira Lagna requirement
    if (shuddhiOptions?.requireSthiraLagna) {
      if (lagnaDetails.modality === 'Sthira') {
        shuddhiBonus += 10;
        reasons.push(`✓ Sthira (Fixed) Lagna (${lagnaDetails.sign}): Bedrock permanence & longevity.`);
      } else {
        shuddhiPenalty += 20;
        cautions.push(`Breaches Sthira requirement (Found: ${lagnaDetails.modality} Lagna in ${lagnaDetails.sign}).`);
      }
    }

    // Chara Lagna requirement
    if (shuddhiOptions?.requireCharaLagna) {
      if (lagnaDetails.modality === 'Chara') {
        shuddhiBonus += 10;
        reasons.push(`✓ Chara (Movable) Lagna (${lagnaDetails.sign}): Mobility, asset turnover & rapid expansion.`);
      } else {
        shuddhiPenalty += 15;
        cautions.push(`Modality mismatch (Expected Chara Lagna, found: ${lagnaDetails.sign}).`);
      }
    }

    // Ashtama Shuddhi
    if (shuddhiOptions?.ashtamaShuddhi) {
      if (lagnaDetails.isAshtamaClean) {
        shuddhiBonus += 8;
        reasons.push(`✓ Ashtama Shuddhi passed: 8th House from Muhurta Lagna is auspiciously vacant.`);
      } else {
        shuddhiPenalty += 25;
        cautions.push(`Ashtama Dosha: 8th House occupied by ${lagnaDetails.ashtamaPlanets.join(', ')}.`);
      }
    }

    // Upachaya Malefics (3, 6, 11)
    if (shuddhiOptions?.upachayaMalefics && lagnaDetails.upachayaMalefics.length > 0) {
      shuddhiBonus += 6;
      reasons.push(`✓ Upachaya Victory: ${lagnaDetails.upachayaMalefics.join(', ')} in 3/6/11H crushes competition.`);
    }

    // Kendra Benefics (1, 4, 7, 10)
    if (shuddhiOptions?.kendraBenefics && lagnaDetails.kendraBenefics.length > 0) {
      shuddhiBonus += 6;
      reasons.push(`✓ Kendra Benefic Blessing: ${lagnaDetails.kendraBenefics.join(', ')} in Kendras brings stability.`);
    }

    // Gandanta Filter
    if (shuddhiOptions?.gandantaFilter && lagnaDetails.isGandanta) {
      shuddhiPenalty += 35;
      cautions.push(`⚠️ Critical ${lagnaDetails.gandantaType || ''} Gandanta Sandhi active.`);
    }

    // Visha Ghati Filter
    if (shuddhiOptions?.vishaGhatiFilter && lagnaDetails.isVishaGhati) {
      shuddhiPenalty += 20;
      cautions.push(`⚠️ Visha Ghati active (toxic nakshatra window).`);
    }
  }

  // Auspicious Yogas (Amrita Siddhi, Guru-Pushya, Sarvartha Siddhi)
  if (specialYogas && specialYogas.length > 0 && shuddhiOptions?.boostSiddhiYogas) {
    specialYogas.forEach(yoga => {
      shuddhiBonus += 10;
      reasons.push(`🌟 ${yoga.name} active (${yoga.multiplierBenefit}).`);
    });
  }

  shuddhiScore = Math.max(0, Math.min(maxShuddhiWeight, 14 + shuddhiBonus - shuddhiPenalty));

  // 7. YOGA & KARANA EVALUATION (Classical Muhurta Rules & Activity Awareness)
  const yogaNameStr = typeof panchanga.yoga === 'string' ? panchanga.yoga : (panchanga.yoga?.name || '');
  const karanaNameStr = typeof panchanga.karana === 'string' ? panchanga.karana : (panchanga.karana?.name || '');
  const yogaRule = evaluateYogaRule(yogaNameStr, activity?.activityId);
  const isVishti = karanaNameStr.toLowerCase().includes('vishti') || karanaNameStr.toLowerCase().includes('bhadra');
  const karanaRule = evaluateKaranaRule(karanaNameStr, isVishti, activity?.activityId);

  // Custom user rules override if specified
  if (customRules?.avoidedYogas && customRules.avoidedYogas.includes(yogaNameStr)) {
    yogaRule.classification = 'RESTRICTIVE';
    yogaRule.severity = 'high';
    yogaRule.explanation = `User custom rule avoids ${yogaNameStr} Yoga.`;
    yogaRule.scoreModifier = -15;
  }
  if (customRules?.avoidedKaranas && customRules.avoidedKaranas.includes(karanaNameStr)) {
    karanaRule.classification = 'RESTRICTIVE';
    karanaRule.severity = 'high';
    karanaRule.explanation = `User custom rule avoids ${karanaNameStr} Karana.`;
    karanaRule.scoreModifier = -15;
  }

  // Derive explicit, non-arbitrary Yoga score from classical classification
  let yogaScore = 5;
  if (yogaRule.classification === 'FAVOURABLE') {
    yogaScore = 8;
    reasons.push(`✓ Favorable ${panchanga.yoga} Yoga: ${yogaRule.explanation}`);
  } else if (yogaRule.classification === 'NEUTRAL') {
    yogaScore = 5;
    reasons.push(`• Neutral ${panchanga.yoga} Yoga.`);
  } else if (yogaRule.classification === 'CAUTION') {
    yogaScore = 3;
    cautions.push(`Caution on ${panchanga.yoga} Yoga: ${yogaRule.explanation}`);
  } else if (yogaRule.classification === 'RESTRICTIVE') {
    yogaScore = 1;
    cautions.push(`⚠️ Restrictive ${panchanga.yoga} Yoga: ${yogaRule.explanation}`);
  } else if (yogaRule.classification === 'HARD_EXCLUSION') {
    yogaScore = 0;
    cautions.push(`⛔ Prohibited ${panchanga.yoga} Yoga: ${yogaRule.explanation}`);
  } else {
    yogaScore = 5;
  }

  // Derive explicit Karana score from classical classification
  let karanaScore = 4;
  if (karanaRule.classification === 'FAVOURABLE') {
    karanaScore = 5;
    reasons.push(`✓ Favorable ${panchanga.karana} Karana: ${karanaRule.explanation}`);
  } else if (karanaRule.classification === 'NEUTRAL') {
    karanaScore = 4;
    reasons.push(`• Neutral ${panchanga.karana} Karana.`);
  } else if (karanaRule.classification === 'CAUTION') {
    karanaScore = 2;
    cautions.push(`Caution on ${panchanga.karana} Karana: ${karanaRule.explanation}`);
  } else if (karanaRule.classification === 'RESTRICTIVE') {
    karanaScore = 0;
    cautions.push(`⚠️ Restrictive ${panchanga.karana} Karana: ${karanaRule.explanation}`);
  } else if (karanaRule.classification === 'HARD_EXCLUSION') {
    karanaScore = 0;
    cautions.push(`⛔ Prohibited ${panchanga.karana} Karana: ${karanaRule.explanation}`);
  } else {
    karanaScore = 3;
  }

  const hasShuddhiOptions = !!shuddhiOptions;
  const rawTotal = (
    taraScore +
    tithiScore +
    nakScore +
    rashiScore +
    varaScore +
    prohibitedScore +
    horaScore +
    yogaScore +
    karanaScore +
    (hasShuddhiOptions ? shuddhiScore : 0)
  );

  const maxPossible = (
    maxTaraWeight +
    maxTithiWeight +
    maxNakWeight +
    maxRashiWeight +
    8 + // vara
    8 + // prohibited (rahu/yamaganda)
    6 + // hora
    8 + // yoga
    5 + // karana
    (hasShuddhiOptions ? maxShuddhiWeight : 0)
  );

  // 10. PHASE 16–19 ADVANCED ASTRONOMICAL, CALENDAR & SAMSKARA INTEGRATION
  const lat = (panchanga as any).coordinates?.latitude ?? (panchanga as any).location?.latitude;
  const lon = (panchanga as any).coordinates?.longitude ?? (panchanga as any).location?.longitude;
  const tzOffset = (panchanga as any).coordinates?.timezoneOffsetMinutes ?? (panchanga as any).location?.timezoneOffsetMinutes;

  const observer = (typeof lat === 'number' && typeof lon === 'number')
    ? { latitude: lat, longitude: lon, timezoneOffsetMinutes: typeof tzOffset === 'number' ? tzOffset : 330 }
    : undefined;
  const phase16Res = evaluatePhase16SolarLunarRules(date, activity?.activityId || 'general', observer);
  const phase17Res = evaluatePhase17CalendarRules(
    date,
    typeof lat === 'number' ? lat : 28.6139,
    typeof lon === 'number' ? lon : 77.2090,
    activity?.activityId || 'general'
  );
  const samskaraKey = activity?.activityId || '';
  const phase18Res = evaluateSamskaraMuhurta(
    date,
    typeof lat === 'number' ? lat : 28.6139,
    typeof lon === 'number' ? lon : 77.2090,
    samskaraKey
  );

  const factorEvidences: FactorEvidence[] = [
    {
      factorId: phase16Res.factorId,
      factorName: phase16Res.factorName,
      classification: phase16Res.classification,
      scoreImpact: phase16Res.scoreImpact,
      description: phase16Res.description
    },
    {
      factorId: phase17Res.factorId,
      factorName: phase17Res.factorName,
      classification: phase17Res.classification,
      scoreImpact: phase17Res.scoreImpact,
      description: phase17Res.description
    }
  ];

  if (phase18Res) {
    // Phase 18 is quarantined as an informational research artifact with zero production score impact.
    // Quarantined factors MUST NOT influence scoring, exclusion, warning or ranking.
    factorEvidences.push({
      factorId: 'FACT_PHASE18_SAMSKARA',
      factorName: `Phase 18 Samskara Rules (${phase18Res.samskaraName}) [QUARANTINED]`,
      classification: 'INFORMATIONAL',
      scoreImpact: 0,
      description: phase18Res.description
    });
  }

  const resolvedResolution = resolveMuhurtaFactors(factorEvidences);
  const finalRawTotal = rawTotal + resolvedResolution.netScore;

  // Normalized cleanly to true 0-100 percentage of maximum possible points
  let totalScore = Math.min(100, Math.max(10, Math.round((finalRawTotal / maxPossible) * 100)));

  if (resolvedResolution.finalClassification === 'HARD_EXCLUSION') {
    totalScore = Math.min(totalScore, 30);
    cautions.push(`⛔ Phase 16-19 Resolution Exclusion: ${resolvedResolution.summary}`);
  } else if (resolvedResolution.finalClassification === 'SUPPORT') {
    reasons.push(`🌟 Phase 16-19 Resolution Support: ${resolvedResolution.summary}`);
  } else if (resolvedResolution.finalClassification === 'WARNING') {
    cautions.push(`⚠️ Phase 16-19 Resolution Warning: ${resolvedResolution.summary}`);
  }


  // Non-negotiable classical safety:
  // If Vishti/Bhadra is HARD_EXCLUSION or a classical Mahadosha is active, candidate must never receive a high score!
  if (karanaRule.classification === 'HARD_EXCLUSION') {
    totalScore = Math.min(totalScore, 35);
    cautions.push(`⛔ Vishti (Bhadra) Dosha: Prohibited for this activity; overall suitability capped to Avoid.`);
  }
  if (yogaRule.classification === 'HARD_EXCLUSION') {
    totalScore = Math.min(totalScore, 35);
    cautions.push(`⛔ Mahadosha (${panchanga.yoga}): Universally prohibited; overall suitability capped to Avoid.`);
  }

  let label: MuhurtaScore['label'] = 'Average';
  if (totalScore >= 85) label = 'Excellent';
  else if (totalScore >= 70) label = 'Very Good';
  else if (totalScore >= 55) label = 'Good';
  else if (totalScore < 40) label = 'Avoid';

  // 8. MULTI-PILLAR INDIVIDUAL SCORING
  const taraPercent = Math.min(100, Math.round((taraScore / maxTaraWeight) * 100));
  const lagnaPercent = Math.min(100, Math.round((shuddhiScore / maxShuddhiWeight) * 100));
  const rashiPercent = Math.min(100, Math.round((rashiScore / maxRashiWeight) * 100));

  const totalPanchangScore = tithiScore + nakScore + varaScore + horaScore + yogaScore + karanaScore;
  const maxPanchangScore = maxTithiWeight + maxNakWeight + 8 + 6 + 8 + 5;
  const panchangPercent = Math.min(100, Math.round((totalPanchangScore / maxPanchangScore) * 100));

  const activeYogasNames = (specialYogas || []).map(y => y.name);
  const isPariharaClean = prohibitedScore === 8;
  const pariharaScore = isPariharaClean ? (10 + (activeYogasNames.length * 5)) : 2;
  const pariharaMax = 10 + Math.max(0, activeYogasNames.length * 5);
  const pariharaPercent = Math.min(100, Math.round((pariharaScore / pariharaMax) * 100));

  const getPillarStatus = (pct: number): 'excellent' | 'good' | 'moderate' | 'weak' => {
    if (pct >= 85) return 'excellent';
    if (pct >= 70) return 'good';
    if (pct >= 50) return 'moderate';
    return 'weak';
  };

  const pillars: MuhurtaPillarsScore = {
    taraBala: {
      score: taraScore,
      max: maxTaraWeight,
      percentage: taraPercent,
      label: taraBala.taraName ? `${taraBala.taraName} Tara` : 'Tara Bala',
      subLabel: taraBala.suitability,
      status: getPillarStatus(taraPercent)
    },
    lagnaShuddhi: {
      score: shuddhiScore,
      max: maxShuddhiWeight,
      percentage: lagnaPercent,
      label: lagnaDetails ? `${lagnaDetails.sign} (${lagnaDetails.modality})` : 'Lagna Shuddhi',
      subLabel: lagnaDetails?.isAshtamaClean ? '8H Vacant • Clean' : (lagnaDetails ? `8H: ${lagnaDetails.ashtamaPlanets.join(', ')}` : undefined),
      status: getPillarStatus(lagnaPercent)
    },
    chandraBala: {
      score: rashiScore,
      max: maxRashiWeight,
      percentage: rashiPercent,
      label: `Moon in ${panchanga.moonRashi}`,
      subLabel: janmaRashiIdxs && janmaRashiIdxs.length > 0 ? 'Transit Gochar' : 'Lunar Strength',
      status: getPillarStatus(rashiPercent)
    },
    panchangaLimbs: {
      score: totalPanchangScore,
      max: maxPanchangScore,
      percentage: panchangPercent,
      label: `${panchanga.tithi} • ${panchanga.nakshatra}`,
      subLabel: `${panchanga.yoga} • ${panchanga.karana}`,
      status: getPillarStatus(panchangPercent)
    },
    pariharaShield: {
      score: pariharaScore,
      max: pariharaMax,
      percentage: pariharaPercent,
      label: isPariharaClean ? 'Rahu/Yama Pure' : 'Afflicted Window',
      subLabel: activeYogasNames.length > 0 ? activeYogasNames.join(', ') : 'Zero Prohibited Overlap',
      status: getPillarStatus(pariharaPercent)
    }
  };

  return {
    totalScore,
    label,
    framework,
    pillars,
    yogaRuleResult: yogaRule,
    karanaRuleResult: karanaRule,
    breakdown: {
      taraBala: taraScore,
      tithi: tithiScore,
      nakshatra: nakScore,
      vara: varaScore,
      hora: horaScore,
      yoga: yogaScore,
      karana: karanaScore,
      moonCondition: rashiScore,
      prohibitedPeriods: prohibitedScore,
      shuddhi: hasShuddhiOptions ? shuddhiScore : undefined
    },
    maxBreakdown: {
      taraBala: maxTaraWeight,
      tithi: maxTithiWeight,
      nakshatra: maxNakWeight,
      vara: 8,
      hora: 6,
      yoga: 8,
      karana: 5,
      moonCondition: maxRashiWeight,
      prohibitedPeriods: 8,
      shuddhi: hasShuddhiOptions ? maxShuddhiWeight : undefined
    },
    reasons,
    cautions
  };
}

// --- TRANSIT & AUSPICIOUS WINDOW CALCULATION HELPERS ---

export function getMoonSiderealLon(date: Date): number {
  return calculateAuthoritativeSunMoon(date).moonLongitude;
}

export function getNakshatraIndexForDate(date: Date): number {
  const lon = getMoonSiderealLon(date);
  return Math.floor(lon / (360 / 27));
}

function findNakshatraBoundary(t1: Date, t2: Date, startNakIdx: number): Date {
  let low = t1.getTime();
  let high = t2.getTime();
  for (let i = 0; i < 14; i++) {
    const mid = Math.floor((low + high) / 2);
    const midNak = getNakshatraIndexForDate(new Date(mid));
    if (midNak === startNakIdx) {
      low = mid;
    } else {
      high = mid;
    }
  }
  return new Date(Math.floor((low + high) / 2));
}

function timesOverlap(start1: Date, end1: Date, start2: Date, end2: Date): boolean {
  return Math.max(start1.getTime(), start2.getTime()) < Math.min(end1.getTime(), end2.getTime());
}

export function getDayAuspiciousChoghadiyas(
  sunrise: Date,
  sunset: Date,
  vara: string,
  rahuKalam?: { rawStart?: Date; rawEnd?: Date },
  yamaganda?: { rawStart?: Date; rawEnd?: Date },
  gulika?: { rawStart?: Date; rawEnd?: Date }
) {
  const VARA_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayIdx = Math.max(0, VARA_NAMES.indexOf(vara));
  const DAY_CHOGHADIYA_CYCLES: ('Amrit' | 'Shubh' | 'Labh' | 'Char' | 'Rog' | 'Kaal' | 'Udveg')[][] = [
    ['Udveg', 'Char', 'Labh', 'Amrit', 'Kaal', 'Shubh', 'Rog', 'Udveg'],       // Sunday
    ['Amrit', 'Kaal', 'Shubh', 'Rog', 'Udveg', 'Char', 'Labh', 'Amrit'],       // Monday
    ['Rog', 'Udveg', 'Char', 'Labh', 'Amrit', 'Kaal', 'Shubh', 'Rog'],         // Tuesday
    ['Labh', 'Amrit', 'Kaal', 'Shubh', 'Rog', 'Udveg', 'Char', 'Labh'],         // Wednesday
    ['Shubh', 'Rog', 'Udveg', 'Char', 'Labh', 'Amrit', 'Kaal', 'Shubh'],       // Thursday
    ['Char', 'Labh', 'Amrit', 'Kaal', 'Shubh', 'Rog', 'Udveg', 'Char'],         // Friday
    ['Kaal', 'Shubh', 'Rog', 'Udveg', 'Char', 'Labh', 'Amrit', 'Kaal']          // Saturday
  ];
  const sr = sunrise.getTime();
  const ss = sunset.getTime();
  const partMs = (ss - sr) / 8;
  const auspiciousList: { name: string; hiName?: string; timeRange: string; rawStart?: Date; rawEnd?: Date; conflict?: string }[] = [];

  for (let i = 0; i < 8; i++) {
    const name = DAY_CHOGHADIYA_CYCLES[dayIdx][i];
    if (name === 'Amrit' || name === 'Shubh' || name === 'Labh') {
      const cStart = new Date(sr + i * partMs);
      const cEnd = new Date(sr + (i + 1) * partMs);
      const hiNames: Record<string, string> = { Amrit: 'अमृत', Shubh: 'शुभ', Labh: 'लाभ' };

      let conflict: string | undefined = undefined;
      if (rahuKalam?.rawStart && rahuKalam?.rawEnd && timesOverlap(cStart, cEnd, rahuKalam.rawStart, rahuKalam.rawEnd)) {
        conflict = 'Rahu Kalam';
      } else if (gulika?.rawStart && gulika?.rawEnd && timesOverlap(cStart, cEnd, gulika.rawStart, gulika.rawEnd)) {
        conflict = 'Gulika';
      } else if (yamaganda?.rawStart && yamaganda?.rawEnd && timesOverlap(cStart, cEnd, yamaganda.rawStart, yamaganda.rawEnd)) {
        conflict = 'Yamaganda';
      }

      auspiciousList.push({
        name,
        hiName: hiNames[name] || name,
        timeRange: `${formatTime(cStart, false)} — ${formatTime(cEnd, false)}`,
        rawStart: cStart,
        rawEnd: cEnd,
        conflict
      });
    }
  }
  return auspiciousList;
}

// --- RECOMMENDATION / SEARCH ENGINE ---

export async function findBestMuhurtas(params: MuhurtaSearchParams, janmaNakIdx?: number, signal?: AbortSignal): Promise<MuhurtaAnalysis[]> {
  const searchStart = new Date(params.startDate);
  searchStart.setHours(0, 0, 0, 0);
  const searchEnd = new Date(params.endDate);
  searchEnd.setHours(23, 59, 59, 999);

  const activity = ACTIVITY_PREFERENCES.find(a => a.activityId === params.activityId) || ACTIVITY_PREFERENCES[ACTIVITY_PREFERENCES.length - 1];
  const effectiveNakIdxs = params.janmaNakIdxs || (janmaNakIdx !== undefined ? [{ name: 'You', idx: janmaNakIdx }] : []);

  // Discover all distinct Moon Nakshatra transits across the search range
  interface TransitPeriod {
    nakIndex: number;
    nakName: string;
    startTime: Date;
    endTime: Date;
  }

  const transits: TransitPeriod[] = [];

  // Step backward from searchStart to locate when the current Nakshatra began
  const initialNak = getNakshatraIndexForDate(searchStart);
  let stepBack = new Date(searchStart);
  while (getNakshatraIndexForDate(stepBack) === initialNak && (searchStart.getTime() - stepBack.getTime()) < 36 * 3600000) {
    stepBack = new Date(stepBack.getTime() - 3600000);
  }
  const firstNakStart = findNakshatraBoundary(stepBack, new Date(stepBack.getTime() + 3600000), getNakshatraIndexForDate(stepBack));

  let currNak = initialNak;
  let currStart = firstNakStart;
  let stepForward = new Date(searchStart);

  // Scan forward past searchEnd to find concluding times of all transits
  const maxScan = new Date(searchEnd.getTime() + 36 * 3600000);
  while (stepForward.getTime() <= maxScan.getTime()) {
    if (signal?.aborted) return [];
    const nextHour = new Date(stepForward.getTime() + 3600000);
    const nextNak = getNakshatraIndexForDate(nextHour);
    if (nextNak !== currNak) {
      const transitionTime = findNakshatraBoundary(stepForward, nextHour, currNak);
      transits.push({
        nakIndex: currNak,
        nakName: NAKSHATRAS[currNak].name,
        startTime: currStart,
        endTime: transitionTime
      });
      currNak = nextNak;
      currStart = transitionTime;
    }
    stepForward = nextHour;
    const maxLimit = params.maxTransits || 20;
    if (transits.length >= maxLimit) break;
  }
  if (transits.length === 0 || transits[transits.length - 1].endTime < maxScan) {
    transits.push({
      nakIndex: currNak,
      nakName: NAKSHATRAS[currNak].name,
      startTime: currStart,
      endTime: maxScan
    });
  }

  const results: MuhurtaAnalysis[] = [];

  for (const transit of transits) {
    if (signal?.aborted) return [];
    await new Promise(resolve => setTimeout(resolve, 0));

    // Check overlap with requested search range
    if (transit.endTime.getTime() <= searchStart.getTime() || transit.startTime.getTime() >= searchEnd.getTime()) {
      continue;
    }

    // Determine effective target Tara types
    let allowedTaras: TaraType[] | undefined = undefined;
    if (params.targetTaraTypes && params.targetTaraTypes.length > 0) {
      allowedTaras = params.targetTaraTypes;
    } else if (params.navataraFilter) {
      if (params.navataraFilter === 'kshema_only') allowedTaras = ['Kshema'];
      else if (params.navataraFilter === 'sadhaka_only') allowedTaras = ['Sadhaka'];
      else if (params.navataraFilter === 'sampat_only') allowedTaras = ['Sampat'];
      else if (params.navataraFilter === 'mitra_only') allowedTaras = ['Mitra', 'Param Mitra'];
    }

    // Evaluate Tara Bala for effective partners
    let taraBala: TaraBalaResult;
    if (effectiveNakIdxs.length > 0) {
      taraBala = calculateTaraBala(effectiveNakIdxs[0].idx, transit.nakIndex);

      // If specific Tara filter is requested, strictly filter by primary native's Tara
      if (allowedTaras && !allowedTaras.includes(taraBala.taraName)) {
        continue;
      }

      // Multi-partner safety check
      if (effectiveNakIdxs.length > 1) {
        const partnerTaras = effectiveNakIdxs.map(p => calculateTaraBala(p.idx, transit.nakIndex));
        const hasDanger = partnerTaras.some(t => t.suitability === 'Danger' || t.suitability === 'Poor');
        if (hasDanger) {
          // If a joint partner has a malefic Tara, discard this transit
          continue;
        }
      }

      // Guard against Chandrashtama (Moon transiting 8th sign from natal Janma Rashi)
      if (params.janmaRashiIdxs && params.janmaRashiIdxs.length > 0 && allowedTaras && allowedTaras.length > 0) {
        const primaryRashi = params.janmaRashiIdxs[0].idx;
        const approxMoonDeg = (transit.nakIndex * (360 / 27)) + 6.666;
        const moonRashiIdx = Math.floor(approxMoonDeg / 30) % 12;
        const rashiDist = ((moonRashiIdx - primaryRashi + 12) % 12) + 1;
        if (rashiDist === 8) {
          // Exclude Chandrashtama when targeting specific auspicious Tara
          continue;
        }
      }
    } else {
      taraBala = {
        taraName: 'General Alignment' as TaraType,
        taraCount: 0,
        meaning: `Auspicious transit for ${activity.label}`,
        suitability: 'Good'
      };
    }

    // Filter out malefic transits (Vipat, Pratyari, Naidhana) in personalized mode
    if (effectiveNakIdxs.length > 0 && (taraBala.suitability === 'Danger' || taraBala.suitability === 'Poor')) {
      continue;
    }

    // Determine operational window boundaries
    let windowStart = new Date(Math.max(transit.startTime.getTime(), searchStart.getTime()));
    if (params.preferredTimeRange) {
      const [sh, sm] = params.preferredTimeRange.start.split(':').map(Number);
      const prefStartToday = new Date(windowStart);
      prefStartToday.setHours(sh, sm, 0, 0);
      if (prefStartToday.getTime() >= windowStart.getTime() && prefStartToday.getTime() < transit.endTime.getTime()) {
        windowStart = prefStartToday;
      }
    }

    const windowEnd = new Date(Math.min(transit.endTime.getTime(), searchEnd.getTime()));

    // Collect daily operational slots (Abhijit, Choghadiyas, Rahu Kalam) for each day in this window
    const dailySlots: any[] = [];
    const dayCursor = new Date(windowStart);
    dayCursor.setHours(0, 0, 0, 0);
    const lastDay = new Date(windowEnd);
    lastDay.setHours(0, 0, 0, 0);

    while (dayCursor.getTime() <= lastDay.getTime()) {
      const d = new Date(dayCursor);
      const dailyPanchang = getDailyPanchangaFactors(d, params.latitude, params.longitude);
      const choghadiyas = getDayAuspiciousChoghadiyas(
        dailyPanchang.sunrise,
        dailyPanchang.sunset,
        dailyPanchang.vara,
        dailyPanchang.rahuKalam,
        dailyPanchang.yamaganda,
        dailyPanchang.gulikaKalam
      );

      let abhijitConflict: string | undefined = undefined;
      if (dailyPanchang.vara === 'Wednesday') {
        abhijitConflict = 'Inauspicious on Wednesday (Budhaditya/Rahu Dosha)';
      }
      if (dailyPanchang.abhijit?.rawStart && dailyPanchang.abhijit?.rawEnd) {
        if (dailyPanchang.rahuKalam?.rawStart && dailyPanchang.rahuKalam?.rawEnd && timesOverlap(dailyPanchang.abhijit.rawStart, dailyPanchang.abhijit.rawEnd, dailyPanchang.rahuKalam.rawStart, dailyPanchang.rahuKalam.rawEnd)) {
          abhijitConflict = abhijitConflict ? `${abhijitConflict}, Rahu Kalam` : 'Rahu Kalam';
        } else if (dailyPanchang.gulikaKalam?.rawStart && dailyPanchang.gulikaKalam?.rawEnd && timesOverlap(dailyPanchang.abhijit.rawStart, dailyPanchang.abhijit.rawEnd, dailyPanchang.gulikaKalam.rawStart, dailyPanchang.gulikaKalam.rawEnd)) {
          abhijitConflict = abhijitConflict ? `${abhijitConflict}, Gulika` : 'Gulika';
        } else if (dailyPanchang.yamaganda?.rawStart && dailyPanchang.yamaganda?.rawEnd && timesOverlap(dailyPanchang.abhijit.rawStart, dailyPanchang.abhijit.rawEnd, dailyPanchang.yamaganda.rawStart, dailyPanchang.yamaganda.rawEnd)) {
          abhijitConflict = abhijitConflict ? `${abhijitConflict}, Yamaganda` : 'Yamaganda';
        }
      }

      const isEndingDay = d.toDateString() === transit.endTime.toDateString();
      const isStartingDay = d.toDateString() === transit.startTime.toDateString();

      let validityNote: string | undefined = undefined;
      if (isEndingDay) {
        validityNote = `Active until ${formatTime(transit.endTime, false)} when ${transit.nakName} Nakshatra concludes.`;
      } else if (isStartingDay && transit.startTime.getTime() > d.getTime()) {
        validityNote = `Transit begins at ${formatTime(transit.startTime, false)}.`;
      }

      dailySlots.push({
        date: d.toISOString().split('T')[0],
        formattedDate: d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        validityNote,
        abhijit: { ...dailyPanchang.abhijit, conflict: abhijitConflict },
        rahuKalam: dailyPanchang.rahuKalam,
        yamaganda: dailyPanchang.yamaganda,
        gulikaKalam: dailyPanchang.gulikaKalam,
        favorableChoghadiyas: choghadiyas
      });

      dayCursor.setDate(dayCursor.getDate() + 1);
    }

    // Representative time for Panchanga & Lagna calculation
    let repTime = new Date(windowStart);
    if (repTime.getHours() < 6 || repTime.getHours() > 19) {
      repTime.setHours(11, 30, 0, 0); // Default to midday
    }

    // If Sthira or Chara Lagna is requested, optimize repTime to find the best rising sign during active hours
    if (params.shuddhiOptions?.requireSthiraLagna || params.shuddhiOptions?.requireCharaLagna || params.shuddhiOptions?.ashtamaShuddhi) {
      const scanDate = new Date(windowStart);
      const scanEndDay = new Date(Math.min(windowEnd.getTime(), windowStart.getTime() + 86400000));
      let bestCandidateDate = repTime;
      let bestCandidateFitness = -9999;
      let foundExactModality = false;

      let startHour = 6;
      let endHour = 20;
      if (params.preferredTimeRange) {
        const [sh] = params.preferredTimeRange.start.split(':').map(Number);
        const [eh] = params.preferredTimeRange.end.split(':').map(Number);
        startHour = Math.max(0, sh);
        endHour = Math.min(23, eh);
      }

      // Check in 20-minute steps across active daytime hours
      for (let h = startHour; h <= endHour; h++) {
        for (const m of [0, 20, 40]) {
          const testDate = new Date(scanDate);
          testDate.setHours(h, m, 0, 0);
          if (testDate.getTime() >= windowStart.getTime() && testDate.getTime() <= scanEndDay.getTime()) {
            const testLagna = calculateMuhurtaLagna(testDate, params.latitude, params.longitude, params.shuddhiOptions);
            let fitness = testLagna.shuddhiScore;

            if (params.shuddhiOptions.requireSthiraLagna) {
              if (testLagna.modality === 'Sthira') {
                fitness += 60;
                foundExactModality = true;
              } else {
                fitness -= 40;
              }
            } else if (params.shuddhiOptions.requireCharaLagna) {
              if (testLagna.modality === 'Chara') {
                fitness += 60;
                foundExactModality = true;
              } else {
                fitness -= 40;
              }
            }

            if (params.shuddhiOptions.ashtamaShuddhi) {
              if (testLagna.isAshtamaClean) fitness += 25;
              else fitness -= 35;
            }

            // Clip sandhi degrees (first/last degree of sign)
            if (testLagna.degreeInSign < 1.0 || testLagna.degreeInSign > 29.0) {
              fitness -= 25;
            }

            if (fitness > bestCandidateFitness) {
              bestCandidateFitness = fitness;
              bestCandidateDate = testDate;
            }
          }
        }
      }

      // If Sthira or Chara was strictly required and not a single daytime hour satisfied it,
      // skip this transit so the results strictly honor Vedic Shuddhi
      if ((params.shuddhiOptions.requireSthiraLagna || params.shuddhiOptions.requireCharaLagna) && !foundExactModality) {
        continue;
      }

      repTime = bestCandidateDate;
    }

    const dailyFactors = getDailyPanchangaFactors(repTime, params.latitude, params.longitude);
    const timeFactors = getTimePanchangaFactors(repTime, dailyFactors);
    const panchanga = { ...dailyFactors, ...timeFactors };

    const lagnaDetails = calculateMuhurtaLagna(repTime, params.latitude, params.longitude, params.shuddhiOptions);
    const specialYogas = detectAuspiciousYogas(panchanga, repTime);

    const score = calculateMuhurtaScore(
      panchanga,
      taraBala,
      activity,
      repTime,
      effectiveNakIdxs,
      params.customRules,
      params.framework || 'combo',
      params.janmaRashiIdxs,
      params.shuddhiOptions,
      lagnaDetails,
      specialYogas
    );

    if (score.totalScore >= 45 && score.label !== 'Avoid') {
      results.push({
        dateTime: repTime.toISOString(),
        startTime: windowStart.toISOString(),
        endTime: windowEnd.toISOString(),
        evaluationSemantics: 'EVALUATED_REPRESENTATIVE_MOMENT',
        evaluatedMoment: repTime.toISOString(),
        panchang: panchanga,
        taraBala,
        score,
        dailySlots,
        lagnaDetails,
        specialYogas,
        transitInfo: {
          nakIndex: transit.nakIndex,
          nakName: transit.nakName,
          taraName: taraBala.taraName,
          taraSuitability: taraBala.suitability,
          transitStart: transit.startTime.toISOString(),
          transitEnd: transit.endTime.toISOString(),
          summaryNote: `${transit.nakName} Nakshatra is active until ${transit.endTime.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}, ${formatTime(transit.endTime, false)}.`
        }
      });
    }
  }

  // Sort deterministically: chronological (default) or score (ACT-MUH-01)
  const sortBy = params.sortBy || 'date';
  results.sort((a, b) => compareMuhurtas(a, b, sortBy));

  const limit = params.limit ?? 8;
  return results.slice(0, limit);
}

export type MuhurtaSortOrder = 'date' | 'score';

/** Numeric comparison that treats equal values (including equal infinities) as a tie. */
function compareNumbers(x: number, y: number): number {
  if (x === y) return 0;
  return x < y ? -1 : 1;
}

/** Valid start timestamp, or +Infinity so missing/invalid dates sink to the tail in date order. */
function sortableStartMs(m: MuhurtaAnalysis): number {
  const ts = m?.startTime || m?.dateTime || m?.evaluatedMoment;
  const t = ts ? new Date(ts).getTime() : NaN;
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
}

/** Valid end timestamp, falling back to the start timestamp. */
function sortableEndMs(m: MuhurtaAnalysis, startMs: number): number {
  const t = m?.endTime ? new Date(m.endTime).getTime() : NaN;
  return Number.isFinite(t) ? t : startMs;
}

/** Finite total score, or -Infinity so missing/invalid scores sink to the tail in score order. */
function sortableScore(m: MuhurtaAnalysis): number {
  const s = m?.score?.totalScore;
  return typeof s === 'number' && Number.isFinite(s) ? s : Number.NEGATIVE_INFINITY;
}

/**
 * Pure, stable comparator for MuhurtaAnalysis objects, shared by every Muhurta result collection.
 * 1. Primary: Score descending ('score') or Start-Time ascending ('date').
 * 2. Secondary: Start-Time ascending ('score') or Score descending ('date').
 * 3. Tertiary: End-Time ascending.
 * 4. Quaternary: lexical dateTime comparison.
 * Missing/invalid scores and dates sort to the tail and never throw.
 */
export function compareMuhurtas(
  a: MuhurtaAnalysis,
  b: MuhurtaAnalysis,
  sortBy: MuhurtaSortOrder = 'date'
): number {
  const timeA = sortableStartMs(a);
  const timeB = sortableStartMs(b);
  const byTime = compareNumbers(timeA, timeB);
  const byScore = compareNumbers(sortableScore(b), sortableScore(a));

  if (sortBy === 'score') {
    if (byScore !== 0) return byScore;
    if (byTime !== 0) return byTime;
  } else {
    if (byTime !== 0) return byTime;
    if (byScore !== 0) return byScore;
  }

  const byEnd = compareNumbers(sortableEndMs(a, timeA), sortableEndMs(b, timeB));
  if (byEnd !== 0) return byEnd;

  return (a?.dateTime || '').localeCompare(b?.dateTime || '');
}

/**
 * Adapter comparator for Next 3 Months wrapped objects.
 */
export function compareFutureMuhurtas(
  a: { muhurta: MuhurtaAnalysis },
  b: { muhurta: MuhurtaAnalysis },
  sortBy: MuhurtaSortOrder = 'date'
): number {
  return compareMuhurtas(a?.muhurta, b?.muhurta, sortBy);
}

/**
 * Deterministically parses the start timestamp in milliseconds for sorting (ACT-MUH-01).
 * Safely falls back to dateTime, evaluatedMoment, or 0 if missing or invalid.
 */
export function getMuhurtaStartTimeMs(m: { startTime?: string; dateTime?: string; evaluatedMoment?: string }): number {
  const ts = m?.startTime || m?.dateTime || m?.evaluatedMoment;
  if (!ts) return 0;
  const t = new Date(ts).getTime();
  return isNaN(t) ? 0 : t;
}

/**
 * PROFILE STORAGE ISOLATION (Requirement 13)
 * Derives canonical profile identity key to prevent cross-profile preference leakage in LocalStorage.
 * Key structure: profileStorageId + feature + preference
 */
export function getProfileStorageId(
  kundali: Partial<CompleteKundali> | { birthDetails?: { name?: string; date?: string; time?: string; latitude?: number; longitude?: number }; id?: string; cloudId?: string; profileStorageId?: string }
): string {
  if (!kundali) return 'default_profile';
  if ((kundali as any).profileStorageId) return (kundali as any).profileStorageId;
  if (kundali.id) return kundali.id;
  if (kundali.cloudId) return kundali.cloudId;
  const b = kundali.birthDetails;
  if (b) {
    const namePart = (b.name || 'unnamed').trim().toLowerCase().replace(/\s+/g, '_');
    const datePart = b.date || 'nodate';
    const timePart = b.time || 'notime';
    const latPart = typeof b.latitude === 'number' ? b.latitude.toFixed(4) : '0';
    const lonPart = typeof b.longitude === 'number' ? b.longitude.toFixed(4) : '0';
    return `${namePart}_${datePart}_${timePart}_${latPart}_${lonPart}`;
  }
  return 'default_profile';
}

export function getProfileScopedStorageKey(
  profileStorageId: string,
  feature: string,
  preference: string
): string {
  return `astro_${profileStorageId}_${feature}_${preference}`;
}

